import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import Layout from '@/components/Layout';
import { formatCurrency, formatCurrencyInput, parseCurrency } from '@/utils/currencyFormat';
import { showToast } from '@/lib/custom-toast';
import {
  CreditCard, Plus, ArrowLeft, Share2, Trash2, Edit3, X,
  User, Building2, Wallet, AlertCircle, CheckCircle2,
  TrendingDown, MessageSquare, Mail, Link,
  Calendar, Sparkles, Zap, Clock, Calculator, ListChecks, Check, Tag,
  Paperclip, Image as ImageIcon, Crown, Eye, Download, ChevronRight,
  Users, ShieldCheck, ThumbsUp, ThumbsDown, MessageCircle, Send, Percent, ArrowLeftRight
} from 'lucide-react';

const CREDITOR_ICONS = {
  person:  { icon: User,       label: 'Persona',  color: '#8b5cf6' },
  bank:    { icon: Building2,  label: 'Banco',    color: '#3b82f6' },
  card:    { icon: CreditCard, label: 'Tarjeta',  color: '#ef4444' },
  service: { icon: Sparkles,   label: 'Servicio', color: '#f59e0b' },
  other:   { icon: Wallet,     label: 'Otro',     color: '#6b7280' },
};

const FREQUENCY_LABELS = {
  none: 'Sin plan (pago libre)',
  weekly: 'Semanal',
  biweekly: 'Quincenal',
  monthly: 'Mensual',
};

const STATUS_CONFIG = {
  active:    { label: 'En progreso', color: 'bg-amber-100 text-amber-700' },
  paid:      { label: 'Liquidada ✓',  color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelada',   color: 'bg-slate-100 text-slate-500' },
};

const COLORS = ['#6366f1','#ef4444','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ec4899','#14b8a6'];
const todayISO = () => new Date().toISOString().split('T')[0];

// Compresor de imagen ultraligero usando Canvas HTML5
const compressReceipt = (file, maxWidth = 1200, quality = 0.75) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

// ─── Modal de Doble Confirmación para Eliminar ─────────────
const ConfirmDeleteModal = ({ title, message, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl space-y-4 border border-slate-100">
        <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center mx-auto">
          <AlertCircle className="w-6 h-6" />
        </div>
        <div className="text-center">
          <h3 className="text-base font-bold text-slate-900">{title || '¿Estás seguro?'}</h3>
          <p className="text-xs text-slate-500 mt-1">{message || 'Esta acción no se puede deshacer.'}</p>
        </div>
        <div className="grid grid-cols-2 gap-2.5 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="py-3 px-4 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-all"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="py-3 px-4 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-bold text-xs transition-all shadow-md shadow-rose-500/25 active:scale-95"
          >
            Sí, eliminar
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal de Confirmación para Validar / Dar Visto Bueno ────
const ConfirmVerifyModal = ({ movement, onConfirm, onCancel, loading }) => {
  if (!movement) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl space-y-4 border border-slate-100">
        <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <div className="text-center">
          <h3 className="text-base font-bold text-slate-900">Dar visto bueno al pago</h3>
          <p className="text-xs text-slate-500 mt-1">
            ¿Confirmas de conformidad haber recibido el abono de <b className="text-emerald-600 font-bold">{formatCurrency(movement.amount)}</b>?
          </p>
          {movement.description && (
            <p className="text-[11px] text-slate-400 mt-1 italic">"{movement.description}"</p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2.5 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="py-3 px-4 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-all disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="py-3 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs transition-all shadow-md shadow-emerald-600/25 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {loading ? 'Validando...' : '✓ Validar pago'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal Deslizable / BottomSheet Táctil (Sin movimiento lateral) ────────
const BottomSheetModal = ({ onClose, children, maxWidth = "sm:max-w-md", title, subtitle, headerExtra }) => {
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const startX = useRef(0);
  const isDragging = useRef(false);
  const [dragY, setDragY] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setDragY(0);
    }, 240);
  }, [onClose]);

  // Bloqueo de scroll del body y ajuste del teclado virtual en móvil
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    const vv = window.visualViewport;
    const handleViewportChange = () => {
      const el = sheetRef.current;
      if (!el || !vv) return;
      const keyboardH = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0));
      if (keyboardH > 50) {
        el.style.paddingBottom = `${keyboardH + 28}px`;
      } else {
        el.style.paddingBottom = '';
      }
    };

    const handleFocusIn = (e) => {
      const target = e.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) {
        setTimeout(() => {
          target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }, 160);
        setTimeout(() => {
          target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }, 320);
      }
    };

    if (vv) {
      vv.addEventListener('resize', handleViewportChange);
      vv.addEventListener('scroll', handleViewportChange);
    }
    window.addEventListener('focusin', handleFocusIn);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.touchAction = originalTouchAction;
      if (vv) {
        vv.removeEventListener('resize', handleViewportChange);
        vv.removeEventListener('scroll', handleViewportChange);
      }
      window.removeEventListener('focusin', handleFocusIn);
    };
  }, []);

  const isFromHandle = useRef(false);

  const handleTouchStart = (e, fromHandle = false) => {
    isFromHandle.current = fromHandle;
    const isAtTop = !sheetRef.current || sheetRef.current.scrollTop <= 0;
    if (fromHandle || isAtTop) {
      startY.current = e.touches[0].clientY;
      startX.current = e.touches[0].clientX;
      isDragging.current = true;
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const currentY = e.touches[0].clientY;
    const currentX = e.touches[0].clientX;
    const diffY = currentY - startY.current;
    const diffX = currentX - startX.current;

    // Si el usuario se desliza más horizontal que vertical, bloquear arrastre hacia abajo
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 8) {
      isDragging.current = false;
      setDragY(0);
      return;
    }

    const isAtTop = !sheetRef.current || sheetRef.current.scrollTop <= 0;
    if (diffY > 0 && (isFromHandle.current || isAtTop)) {
      setDragY(diffY);
    } else if (diffY < 0) {
      isDragging.current = false;
      setDragY(0);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    // Si es desde la barrita: 75px. Si es desde el cuerpo: 190px
    const threshold = isFromHandle.current ? 75 : 190;
    if (dragY > threshold) {
      handleClose();
    } else {
      setDragY(0);
    }
    startY.current = 0;
    startX.current = 0;
    isFromHandle.current = false;
  };

  return (
    <div
      onTouchMove={(e) => { if (e.target === e.currentTarget) e.preventDefault(); }}
      style={{ touchAction: 'pan-y', overflowX: 'hidden' }}
      className={`fixed inset-0 z-50 flex flex-col justify-end sm:justify-center sm:items-center bg-slate-950/60 backdrop-blur-sm transition-opacity duration-300 p-0 sm:p-4 max-w-full overflow-x-hidden ${isClosing ? 'opacity-0' : 'opacity-100 animate-fade-in'}`}
    >
      <div className="hidden sm:block absolute inset-0 -z-10" onClick={handleClose} />
      <div className="flex-1 sm:hidden" onClick={handleClose} />

      <div
        ref={sheetRef}
        onTouchStart={(e) => handleTouchStart(e, false)}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: isClosing ? 'translateY(100%)' : (dragY > 0 ? `translate3d(0, ${dragY}px, 0)` : 'translate3d(0, 0, 0)'),
          transition: isDragging.current ? 'none' : 'transform 0.26s cubic-bezier(0.16, 1, 0.3, 1)',
          touchAction: 'pan-y',
          overflowX: 'hidden'
        }}
        className={`w-full ${maxWidth} bg-white sm:rounded-3xl rounded-t-[2.5rem] shadow-2xl max-h-[92vh] overflow-y-auto no-scrollbar border-t border-white/40 will-change-transform flex flex-col mx-auto max-w-full`}
      >
        {/* Barra superior de arrastre móvil */}
        <div
          onTouchStart={(e) => handleTouchStart(e, true)}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ touchAction: 'none' }}
          className="sm:hidden pt-2.5 pb-1 select-none cursor-grab active:cursor-grabbing flex justify-center"
        >
          <div className="w-12 h-1.5 bg-slate-300 rounded-full" />
        </div>

        {/* Cabecera */}
        {(title || headerExtra) && (
          <div className="flex items-center justify-between px-5 pt-3 pb-3 border-b border-slate-100 sticky top-0 bg-white z-10">
            <div>
              {title && <h3 className="font-bold text-slate-900 text-lg">{title}</h3>}
              {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-2">
              {headerExtra}
              <button
                type="button"
                onClick={handleClose}
                className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-900 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {children}
      </div>
    </div>
  );
};

// ─── Modal Enviar Recibo / Liquidación (Correo & WhatsApp) ───
const SendReceiptModal = ({ debt, totalPaid, onClose }) => {
  const { token } = useContext(AuthContext);
  const [channel, setChannel] = useState('whatsapp'); // 'whatsapp' | 'email'
  const [email, setEmail] = useState(debt?.creditor_email || '');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const frontendUrl = window.location.origin;

  const isPaid = debt.status === 'paid' || debt.current_balance <= 0;
  const shareLink = debt.share_token ? `${frontendUrl}/public/debt/${debt.share_token}` : `${frontendUrl}/debts`;

  const interestInfo = debt.has_interest
    ? `\n⚡ *Tasa de Interés:* ${debt.interest_rate}% mensual\n💸 *Intereses Pagados:* ${formatCurrency(debt.total_interest_paid || 0)}\n⏳ *Intereses Pendientes:* ${formatCurrency(debt.accumulated_interest || 0)}`
    : '';

  const totalDebtAmount = debt.total_amount || debt.initial_amount || 0;

  const waMessage = isPaid
    ? `✅ *COMPROBANTE DE LIQUIDACIÓN DE DEUDA*\n\n¡Hola! Te comparto que la deuda *${debt.name}* ha sido *LIQUIDADA EN SU TOTALIDAD* 🎉.\n\n💰 *Total Liquidado:* ${formatCurrency(totalDebtAmount)}${interestInfo}\n💳 *Saldo Restante:* $0.00\n📅 *Fecha:* ${new Date().toLocaleDateString('es-MX')}\n\nPuedes consultar el historial completo y comprobantes en el enlace:\n🔗 ${shareLink}`
    : `📄 *ESTADO DE CUENTA / RECIBO DE DEUDA*\n\n¡Hola! Te comparto la actualización de la deuda *${debt.name}*:\n\n💰 *Monto Total:* ${formatCurrency(totalDebtAmount)}${interestInfo}\n💚 *Total Pagado:* ${formatCurrency(totalPaid)}\n⏳ *Saldo Pendiente para Liquidar:* ${formatCurrency(debt.current_balance)}\n\nPuedes revisar el detalle de abonos y sellar de conformidad aquí:\n🔗 ${shareLink}`;

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/debts/${debt.id}/send-receipt`, {
        recipient_email: email.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      showToast.success(`Recibo enviado exitosamente a ${email.trim()}`);
      onClose();
    } catch (err) {
      showToast.error(err?.response?.data?.detail || 'Error al enviar recibo por correo');
    } finally {
      setLoading(false);
    }
  };

  const handleSendWhatsApp = () => {
    const cleanPhone = phone.replace(/\D/g, '');
    const encodedText = encodeURIComponent(waMessage);
    const waUrl = cleanPhone
      ? `https://wa.me/${cleanPhone}?text=${encodedText}`
      : `https://api.whatsapp.com/send?text=${encodedText}`;
    window.open(waUrl, '_blank');
    onClose();
  };

  return (
    <BottomSheetModal
      onClose={onClose}
      title={isPaid ? "Comprobante de Liquidación" : "Enviar Recibo de Deuda"}
      subtitle={debt?.name}
    >
      <div className="p-5 space-y-4">
        {/* Selector de Canal */}
        <div className="grid grid-cols-3 gap-2 bg-slate-100 p-1 rounded-2xl">
          <button
            type="button"
            onClick={() => setChannel('whatsapp')}
            className={`py-2 px-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 ${
              channel === 'whatsapp' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-600'
            }`}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            <span>WhatsApp</span>
          </button>
          <button
            type="button"
            onClick={() => setChannel('email')}
            className={`py-2 px-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 ${
              channel === 'email' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-600'
            }`}
          >
            <Mail className="w-3.5 h-3.5" />
            <span>Correo</span>
          </button>
          <button
            type="button"
            onClick={() => {
              if (shareLink) {
                navigator.clipboard?.writeText(shareLink);
                showToast.success('¡Enlace público copiado al portapapeles!');
                onClose();
              }
            }}
            className="py-2 px-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 bg-white hover:bg-slate-50 text-indigo-700 border border-slate-200 shadow-sm active:scale-95"
          >
            <Link className="w-3.5 h-3.5" />
            <span>Copiar Link</span>
          </button>
        </div>

        {channel === 'whatsapp' ? (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
                Número de WhatsApp (opcional con lada)
              </label>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none text-sm font-medium"
                placeholder="Ej: 5214491234567 (o dejar vacío para elegir contacto)"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
                Mensaje a enviar
              </label>
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3.5 text-xs text-emerald-950 whitespace-pre-wrap font-sans leading-relaxed max-h-48 overflow-y-auto">
                {waMessage}
              </div>
            </div>

            <button
              type="button"
              onClick={handleSendWhatsApp}
              className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl shadow-lg shadow-emerald-600/25 active:scale-95 transition-all flex items-center justify-center gap-2 text-sm"
            >
              <MessageCircle className="w-4 h-4" />
              {isPaid ? 'Enviar Liquidación por WhatsApp' : 'Enviar Recibo por WhatsApp'}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSendEmail} className="space-y-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
                Correo Electrónico Destinatario *
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none text-sm font-medium"
                  placeholder="destinatario@correo.com"
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-3.5 text-xs text-indigo-900 space-y-1">
              <p className="font-bold flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                Recibo formal HTML
              </p>
              <p className="text-[11px] text-slate-600">
                Se enviará un resumen completo con todos los abonos, desglose de capital e intereses y comprobantes con el sello oficial de Portal de Gastos.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-2xl shadow-lg active:scale-95 transition-all disabled:opacity-60 flex items-center justify-center gap-2 text-sm"
            >
              <Send className="w-4 h-4" />
              {loading ? 'Enviando...' : (isPaid ? 'Enviar Comprobante de Liquidación' : 'Enviar Recibo por Correo')}
            </button>
          </form>
        )}
      </div>
    </BottomSheetModal>
  );
};

// ─── Modal Visor de Comprobante (Lightbox) ────────────────
const ReceiptModal = ({ receiptUrl, onClose }) => {
  if (!receiptUrl) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-lg bg-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-white/10 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-slate-950">
          <div className="flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-emerald-400" />
            <h3 className="text-white font-black text-sm">Comprobante de Pago</h3>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={receiptUrl}
              download="comprobante-pago.jpg"
              className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-all"
              title="Descargar"
            >
              <Download className="w-4 h-4" />
            </a>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-3 flex items-center justify-center bg-black/40">
          <img src={receiptUrl} alt="Comprobante" className="max-w-full max-h-[70vh] object-contain rounded-xl shadow-lg" />
        </div>
      </div>
    </div>
  );
};

// ─── Modal para Agregar o Editar Concepto del Desglose ────
const ItemModal = ({ debt, itemToEdit, onClose, onSaved }) => {
  const { token } = useContext(AuthContext);
  const [form, setForm] = useState({
    title: itemToEdit?.title || '',
    amount: itemToEdit?.amount ? String(itemToEdit.amount) : '',
    notes: itemToEdit?.notes || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.amount) return;
    setLoading(true);
    try {
      if (itemToEdit?.id) {
        const res = await axios.put(`${API}/debts/${debt.id}/items/${itemToEdit.id}`, {
          title: form.title.trim(),
          amount: parseFloat(form.amount),
          notes: form.notes.trim() ? form.notes.trim() : null
        }, { headers: { Authorization: `Bearer ${token}` } });
        onSaved(res.data);
        showToast.success('Concepto actualizado ✓');
      } else {
        const res = await axios.post(`${API}/debts/${debt.id}/items`, {
          title: form.title.trim(),
          amount: parseFloat(form.amount),
          notes: form.notes.trim() ? form.notes.trim() : null
        }, { headers: { Authorization: `Bearer ${token}` } });
        onSaved(res.data);
        showToast.success('Concepto agregado al desglose ✓');
      }
      onClose();
    } catch {
      showToast.error('Error al guardar concepto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BottomSheetModal
      onClose={onClose}
      title={itemToEdit?.id ? "Editar Concepto" : "Agregar Concepto"}
      subtitle="¿En qué se usó esta parte de la deuda?"
    >
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Concepto / Motivo *</label>
          <input
            type="text"
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 text-sm font-medium"
            placeholder="Ej: Préstamo celular, Cena, Gasolina..."
            required
            autoFocus
          />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Monto *</label>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={form.amount ? formatCurrencyInput(form.amount) : ''}
              onChange={e => setForm(f => ({ ...f, amount: parseCurrency(e.target.value) }))}
              className="w-full pl-8 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none font-black text-slate-900 text-base"
              placeholder="0.00"
              required
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Detalle o nota (opcional)</label>
          <input
            type="text"
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none text-xs"
            placeholder="Detalles adicionales..."
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-2xl shadow-lg active:scale-95 transition-all disabled:opacity-60"
        >
          {loading ? 'Guardando...' : (itemToEdit?.id ? 'Guardar Cambios' : 'Guardar Concepto')}
        </button>
      </form>
    </BottomSheetModal>
  );
};

// ─── Modal Movimiento con Comprobante y Desglose de Interés / Capital ────
const MovementModal = ({ debt, movementToEdit, comments = [], items = [], movements = [], onClose, onSaved }) => {
  const { token, user } = useContext(AuthContext);
  const isPro = Boolean(
    user?.is_admin ||
    user?.subscription_tier === 'pro' ||
    user?.subscription_tier === 'premium' ||
    user?.is_pro ||
    user?.is_premium ||
    ['pro', 'premium'].includes(user?.subscription_plan)
  );

  const unresolvedComments = (comments || []).filter(c => c.comment_type === 'inconforme' && !c.is_resolved);
  const [selectedCommentIds, setSelectedCommentIds] = useState([]);

  const isDebtor = debt?.debt_direction !== 'they_owe_me';
  const initialKind = movementToEdit ? (movementToEdit.type === 'charge' ? 'charge' : (movementToEdit.is_extra ? 'extra' : 'regular')) : 'regular';
  const [kind, setKind] = useState((isDebtor && initialKind === 'charge') ? 'regular' : initialKind);
  const [form, setForm] = useState({
    amount: movementToEdit ? String(movementToEdit.amount) : (debt?.installment_amount ? String(debt.installment_amount) : ''),
    description: movementToEdit ? movementToEdit.description : (debt?.payment_frequency !== 'none' ? `Pago cuota ${FREQUENCY_LABELS[debt?.payment_frequency] || ''}` : 'Pago / Abono'),
    date: movementToEdit ? (movementToEdit.date?.split('T')[0] || todayISO()) : todayISO(),
    notes: movementToEdit?.notes || '',
    receipt_url: movementToEdit?.receipt_url || null,
    debt_item_id: movementToEdit?.debt_item_id || '',
    interest_covered: movementToEdit?.interest_covered !== undefined ? String(movementToEdit.interest_covered) : '',
    principal_covered: movementToEdit?.principal_covered !== undefined ? String(movementToEdit.principal_covered) : ''
  });
  const [selectedDebtItemIds, setSelectedDebtItemIds] = useState(
    (movementToEdit?.debt_item_ids && Array.isArray(movementToEdit.debt_item_ids) && movementToEdit.debt_item_ids.length > 0)
      ? movementToEdit.debt_item_ids
      : (movementToEdit?.debt_item_id ? [movementToEdit.debt_item_id] : [])
  );
  const toggleDebtItem = (id) => {
    setSelectedDebtItemIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Cobertura y saldo pendiente por cada concepto del desglose
  const itemsWithStatus = useMemo(() => {
    if (!items || items.length === 0) return [];
    const directPaymentsByItem = {};
    let genericPool = 0;
    (movements || []).forEach(m => {
      if (movementToEdit && m.id === movementToEdit.id) return;
      if (m.type === 'payment') {
        if (m.debt_item_ids && Array.isArray(m.debt_item_ids) && m.debt_item_ids.length > 0) {
          const splitAmount = (m.amount || 0) / m.debt_item_ids.length;
          m.debt_item_ids.forEach(itemId => {
            directPaymentsByItem[itemId] = (directPaymentsByItem[itemId] || 0) + splitAmount;
          });
        } else if (m.debt_item_id) {
          directPaymentsByItem[m.debt_item_id] = (directPaymentsByItem[m.debt_item_id] || 0) + (m.amount || 0);
        } else {
          genericPool += (m.amount || 0);
        }
      }
    });

    let remainingPool = genericPool;
    return items.map(item => {
      const direct = directPaymentsByItem[item.id] || 0;
      const neededAfterDirect = Math.max(0, item.amount - direct);
      const fromGeneric = Math.min(neededAfterDirect, Math.max(0, remainingPool));
      remainingPool -= fromGeneric;
      const covered = Math.min(item.amount, direct + fromGeneric);
      const remaining = Math.max(0, item.amount - covered);
      const isCovered = remaining <= 0.01 && item.amount > 0;
      return {
        ...item,
        covered,
        remaining,
        isCovered
      };
    });
  }, [items, movements, movementToEdit]);

  const pendingItems = useMemo(() => {
    return itemsWithStatus.filter(it => !it.isCovered || selectedDebtItemIds.includes(it.id));
  }, [itemsWithStatus, selectedDebtItemIds]);

  const liquidatedItems = useMemo(() => {
    return itemsWithStatus.filter(it => it.isCovered && !selectedDebtItemIds.includes(it.id));
  }, [itemsWithStatus, selectedDebtItemIds]);

  const [compressing, setCompressing] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleKindChange = (newKind) => {
    setKind(newKind);
    if (newKind === 'regular') {
      setForm(f => ({
        ...f,
        description: `Pago cuota ${FREQUENCY_LABELS[debt?.payment_frequency] || 'regular'}`
      }));
    } else if (newKind === 'extra') {
      setForm(f => ({ ...f, description: '⚡ Abono extraordinario' }));
    } else if (newKind === 'charge') {
      setForm(f => ({ ...f, description: 'Nuevo préstamo / aumento' }));
    }
  };

  const handleTotalAmountChange = (val) => {
    setForm(f => {
      const tot = parseFloat(val) || 0;
      if (debt?.has_interest && kind !== 'charge') {
        const curPrinc = parseFloat(f.principal_covered) || 0;
        const curInt = parseFloat(f.interest_covered) || 0;
        if (curPrinc > 0 && curInt === 0) {
          return { ...f, amount: val, principal_covered: val };
        } else if (curInt > 0 && curPrinc === 0) {
          return { ...f, amount: val, interest_covered: val };
        }
      }
      return { ...f, amount: val };
    });
  };

  const handlePrincipalChange = (val) => {
    setForm(f => {
      const princ = parseFloat(val) || 0;
      const intVal = parseFloat(f.interest_covered) || 0;
      const total = princ + intVal;
      return {
        ...f,
        principal_covered: val,
        amount: total > 0 ? String(total) : f.amount
      };
    });
  };

  const handleInterestChange = (val) => {
    setForm(f => {
      const intVal = parseFloat(val) || 0;
      const princ = parseFloat(f.principal_covered) || 0;
      const total = princ + intVal;
      return {
        ...f,
        interest_covered: val,
        amount: total > 0 ? String(total) : f.amount
      };
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isPro) {
      showToast.error('Adjuntar comprobantes es exclusivo de usuarios Pro y Premium 👑');
      return;
    }
    setCompressing(true);
    try {
      const compressed = await compressReceipt(file);
      setForm(f => ({ ...f, receipt_url: compressed }));
      showToast.success('Comprobante comprimido y adjunto ✓');
    } catch {
      showToast.error('Error al procesar comprobante');
    } finally {
      setCompressing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.description) return;
    setLoading(true);
    try {
      const type = kind === 'charge' ? 'charge' : 'payment';
      const is_extra = kind === 'extra';
      const payload = {
        amount: parseFloat(form.amount),
        type,
        is_extra,
        description: form.description.trim(),
        date: form.date,
        notes: form.notes?.trim() || null,
        receipt_url: form.receipt_url || null,
        debt_item_id: (kind !== 'charge' && selectedDebtItemIds.length === 1) ? selectedDebtItemIds[0] : null,
        debt_item_ids: (kind !== 'charge' && selectedDebtItemIds.length > 0) ? selectedDebtItemIds : null,
        resolves_comment_ids: selectedCommentIds.length > 0 ? selectedCommentIds : undefined
      };

      if (type === 'payment' && debt?.has_interest) {
        if (form.interest_covered !== '') payload.interest_covered = parseFloat(form.interest_covered);
        if (form.principal_covered !== '') payload.principal_covered = parseFloat(form.principal_covered);
      }

      const res = movementToEdit?.id
        ? await axios.put(`${API}/debts/${debt.id}/movements/${movementToEdit.id}`, payload, { headers: { Authorization: `Bearer ${token}` } })
        : await axios.post(`${API}/debts/${debt.id}/movements`, payload, { headers: { Authorization: `Bearer ${token}` } });

      onSaved(res.data);
      onClose();
      showToast.success(movementToEdit?.id ? 'Movimiento actualizado exitosamente' : (kind === 'charge' ? 'Cargo agregado a la deuda' : 'Pago registrado exitosamente'));
    } catch (err) {
      showToast.error(err?.response?.data?.detail || 'Error al registrar movimiento');
    } finally {
      setLoading(false);
    }
  };

  const totAmount = parseFloat(form.amount) || 0;

  return (
    <BottomSheetModal
      onClose={onClose}
      title={movementToEdit?.id ? "Editar Movimiento" : "Registrar Movimiento"}
      subtitle={debt?.name}
    >
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        {/* Selector de Tipo */}
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Tipo de Movimiento</label>
          <div className={`grid gap-2 ${isDebtor ? 'grid-cols-2' : 'grid-cols-3'}`}>
            <button
              type="button"
              onClick={() => handleKindChange('regular')}
              className={`py-2.5 px-3 rounded-2xl text-xs font-bold transition-all flex flex-col items-center gap-1 ${
                kind === 'regular'
                  ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/25 scale-[1.02]'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Pago Normal</span>
            </button>
            <button
              type="button"
              onClick={() => handleKindChange('extra')}
              className={`py-2.5 px-3 rounded-2xl text-xs font-bold transition-all flex flex-col items-center gap-1 ${
                kind === 'extra'
                  ? 'bg-amber-500 text-white shadow-md shadow-amber-500/25 scale-[1.02]'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Abono Extra</span>
            </button>
            {!isDebtor && (
              <button
                type="button"
                onClick={() => handleKindChange('charge')}
                className={`py-2.5 px-3 rounded-2xl text-xs font-bold transition-all flex flex-col items-center gap-1 ${
                  kind === 'charge'
                    ? 'bg-rose-500 text-white shadow-md shadow-rose-500/25 scale-[1.02]'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                <Plus className="w-4 h-4" />
                <span>Pedir Más</span>
              </button>
            )}
          </div>
        </div>

        {/* Monto Total */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Monto Total del Pago *</label>
            {debt?.installment_amount && kind === 'regular' && (
              <span className="text-[10px] text-indigo-600 font-bold">Cuota pactada: {formatCurrency(debt.installment_amount)}</span>
            )}
          </div>
          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={form.amount ? formatCurrencyInput(form.amount) : ''}
              onChange={e => handleTotalAmountChange(parseCurrency(e.target.value))}
              className="w-full pl-8 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 font-black text-slate-900 text-lg"
              placeholder="0.00"
              required
            />
          </div>
        </div>

        {/* Apartado Directo y Claro: Cuánto a Capital y Cuánto a Interés */}
        {debt?.has_interest && kind !== 'charge' && (
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 space-y-3 animate-fade-in shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-black text-amber-950 uppercase tracking-wider flex items-center gap-1.5">
                <Percent className="w-4 h-4 text-amber-600" />
                Desglose: Capital vs Interés
              </span>
              <span className="text-[10px] text-amber-800 font-bold bg-amber-100 px-2 py-0.5 rounded-full">
                Tasa {debt.interest_rate}% mensual
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold text-slate-700 mb-1 block">
                  💰 Abono a Capital ($)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.principal_covered ? formatCurrencyInput(form.principal_covered) : ''}
                    onChange={e => handlePrincipalChange(parseCurrency(e.target.value))}
                    className="w-full pl-7 pr-3 py-2.5 bg-white border border-amber-300 rounded-xl font-black text-emerald-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold text-slate-700 mb-1 block">
                  ⚡ Pago de Interés ($)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.interest_covered ? formatCurrencyInput(form.interest_covered) : ''}
                    onChange={e => handleInterestChange(parseCurrency(e.target.value))}
                    className="w-full pl-7 pr-3 py-2.5 bg-white border border-amber-300 rounded-xl font-black text-amber-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Accesos rápidos de desglose */}
            {totAmount > 0 && (
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, interest_covered: String(totAmount), principal_covered: '0' }))}
                  className="flex-1 py-1.5 bg-white border border-amber-200 text-amber-900 rounded-xl text-[10px] font-bold hover:bg-amber-100 transition-all shadow-xs"
                >
                  ⚡ Todo a Interés ({formatCurrency(totAmount)})
                </button>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, interest_covered: '0', principal_covered: String(totAmount) }))}
                  className="flex-1 py-1.5 bg-white border border-amber-200 text-amber-900 rounded-xl text-[10px] font-bold hover:bg-amber-100 transition-all shadow-xs"
                >
                  💰 Todo a Capital ({formatCurrency(totAmount)})
                </button>
              </div>
            )}
          </div>
        )}

        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Concepto</label>
          <input
            type="text"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none text-sm font-medium"
            placeholder="Ej: Pago quincenal..."
            required
          />
        </div>

        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Fecha de aplicación</label>
          <input
            type="date"
            value={form.date}
            onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none text-sm font-medium"
          />
        </div>

        {/* Selector de Conceptos del Desglose al que se abona */}
        {items && items.length > 0 && kind !== 'charge' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
                Abonar a concepto(s) específico(s)
              </label>
              {selectedDebtItemIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedDebtItemIds([])}
                  className="text-[11px] text-indigo-600 font-bold hover:underline"
                >
                  Restablecer a General
                </button>
              )}
            </div>

            {selectedDebtItemIds.length === 0 && (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between text-xs text-slate-600">
                <span className="font-semibold">🌐 Distribución automática en orden (Default)</span>
                <span className="text-[10px] text-slate-400 font-medium">
                  {pendingItems.length > 0 ? `${pendingItems.length} concepto(s) pendiente(s)` : 'Todos cubiertos'}
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 gap-1.5 max-h-44 overflow-y-auto pr-1">
              {pendingItems.map(item => {
                const isSelected = selectedDebtItemIds.includes(item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => toggleDebtItem(item.id)}
                    className={`p-2.5 px-3 rounded-2xl border transition-all cursor-pointer flex items-center justify-between ${
                      isSelected
                        ? 'bg-indigo-50/80 border-indigo-300 text-indigo-950 font-bold'
                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 cursor-pointer pointer-events-none"
                      />
                      <div className="min-w-0">
                        <span className="text-xs truncate block">{item.title}</span>
                        {item.covered > 0 && (
                          <span className="text-[10px] text-emerald-600 font-medium block">
                            Abonado: {formatCurrency(item.covered)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <span className="text-xs font-black text-slate-900 block">
                        {formatCurrency(item.remaining)}
                      </span>
                      <span className="text-[9px] text-slate-400 block uppercase">
                        Pendiente
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {liquidatedItems.length > 0 && (
              <p className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1 mt-1">
                ✓ {liquidatedItems.length} concepto{liquidatedItems.length > 1 ? 's' : ''} ya liquidado{liquidatedItems.length > 1 ? 's' : ''} ({liquidatedItems.map(i => i.title).join(', ')})
              </p>
            )}

            {selectedDebtItemIds.length === 1 && (
              <p className="text-[11px] text-indigo-600 font-semibold">
                ✓ Este abono se aplicará a "{items.find(i => i.id === selectedDebtItemIds[0])?.title}"
              </p>
            )}
            {selectedDebtItemIds.length > 1 && (
              <p className="text-[11px] text-indigo-600 font-semibold">
                ✓ El pago de {formatCurrency(totAmount)} se distribuirá entre los {selectedDebtItemIds.length} conceptos seleccionados.
              </p>
            )}
          </div>
        )}

        {/* Adjuntar Comprobante */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5 text-indigo-500" />
              Comprobante de Pago
            </label>
            {!isPro && (
              <span className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full font-bold flex items-center gap-1 border border-amber-200">
                <Crown className="w-3 h-3 text-amber-500" /> Pro / Premium
              </span>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />

          {form.receipt_url ? (
            <div className="relative rounded-2xl border border-indigo-200 p-2.5 bg-indigo-50/50 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <img src={form.receipt_url} alt="Preview" className="w-12 h-12 rounded-xl object-cover border" />
                <div className="min-w-0">
                  <p className="text-xs font-bold text-indigo-950 truncate">Comprobante adjunto ✓</p>
                  <p className="text-[10px] text-indigo-500 font-medium">Listo para guardar</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, receipt_url: null }))}
                className="w-7 h-7 rounded-full bg-red-100 text-red-600 flex items-center justify-center hover:bg-red-200 transition-all shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (!isPro) {
                  showToast.error('Adjuntar comprobantes es exclusivo para usuarios Pro o Premium 👑');
                  return;
                }
                fileInputRef.current?.click();
              }}
              disabled={compressing}
              className="w-full py-3 px-4 border border-dashed border-slate-300 rounded-2xl flex items-center justify-center gap-2 text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all"
            >
              <ImageIcon className="w-4 h-4 text-indigo-500" />
              {compressing ? 'Comprimiendo imagen...' : (isPro ? 'Subir Foto de Comprobante' : 'Subir Comprobante (Exclusivo Pro 👑)')}
            </button>
          )}
        </div>

        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Notas (opcional)</label>
          <textarea
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none resize-none text-xs"
            rows={2}
            placeholder="Folio de transferencia, referencia..."
          />
        </div>

        {/* Vinculación de Aclaraciones Pendientes */}
        {unresolvedComments.length > 0 && (
          <div className="bg-amber-50/80 border border-amber-200 rounded-2xl p-3.5 space-y-2">
            <div className="flex items-center gap-1.5 text-amber-900 font-bold text-xs">
              <Link className="w-3.5 h-3.5 text-amber-600" />
              <span>¿Este movimiento resuelve alguna aclaración del acreedor?</span>
            </div>
            <p className="text-[11px] text-amber-800/80 leading-tight">
              Selecciona las aclaraciones pendientes que se cubren con este movimiento:
            </p>
            <div className="space-y-1.5 max-h-36 overflow-y-auto pt-1">
              {unresolvedComments.map(c => (
                <label key={c.id} className="flex items-start gap-2.5 text-xs text-amber-950 cursor-pointer p-2 bg-white rounded-xl border border-amber-200/70 hover:border-amber-300 transition-all shadow-xs">
                  <input
                    type="checkbox"
                    checked={selectedCommentIds.includes(c.id)}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedCommentIds([...selectedCommentIds, c.id]);
                      } else {
                        setSelectedCommentIds(selectedCommentIds.filter(id => id !== c.id));
                      }
                    }}
                    className="mt-0.5 rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold">{c.author_name}: <span className="font-normal italic">"{c.content}"</span></p>
                    <p className="text-[10px] text-amber-600/75 mt-0.5">{c.created_at?.split('T')[0]}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || compressing}
          className={`w-full py-3.5 text-white font-bold rounded-2xl shadow-lg active:scale-95 transition-all disabled:opacity-60 ${
            kind === 'extra'
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 shadow-amber-500/25'
              : (kind === 'charge' ? 'bg-gradient-to-r from-rose-500 to-red-600 shadow-rose-500/25' : 'bg-gradient-to-r from-emerald-600 to-teal-600 shadow-emerald-500/25')
          }`}
        >
          {loading ? 'Guardando...' : (movementToEdit?.id ? 'Guardar Cambios' : (kind === 'extra' ? 'Registrar Abono Extra ⚡' : (kind === 'charge' ? 'Aumentar Deuda' : 'Guardar Pago')))}
        </button>
      </form>
    </BottomSheetModal>
  );
};

// ─── Modal Crear / Editar Deuda con Plan de Pagos, Intereses y Colaboradores ─────────
const DebtModal = ({ debt, initialDirection = 'i_owe', onClose, onSaved }) => {
  const { token } = useContext(AuthContext);
  const [form, setForm] = useState({
    name: debt?.name || '',
    creditor_type: debt?.creditor_type || 'person',
    debt_direction: debt?.debt_direction || initialDirection,
    creditor_email: debt?.creditor_email || '',
    collaborators: debt?.collaborators || [],
    total_amount: debt?.initial_amount ? String(debt.initial_amount) : (debt?.total_amount ? String(debt.total_amount) : ''),
    currency: debt?.currency || 'MXN',
    description: debt?.description || '',
    color: debt?.color || '#6366f1',
    payment_frequency: debt?.payment_frequency || 'monthly',
    total_installments: debt?.total_installments ? String(debt.total_installments) : '',
    installment_amount: debt?.installment_amount ? String(debt.installment_amount) : '',
    start_date: debt?.start_date || todayISO(),
    due_date: debt?.due_date || '',
    auto_reminder: Boolean(debt?.auto_reminder),
    has_interest: Boolean(debt?.has_interest),
    interest_rate: debt?.interest_rate ? String(debt.interest_rate) : '',
  });
  const [collaboratorInput, setCollaboratorInput] = useState('');
  const [collaboratorToRemove, setCollaboratorToRemove] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAddCollaborator = async (e) => {
    e?.preventDefault?.();
    const email = collaboratorInput.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (form.collaborators.includes(email)) {
      setCollaboratorInput('');
      return;
    }
    const newCollaborators = [...form.collaborators, email];
    setForm(f => ({ ...f, collaborators: newCollaborators }));
    setCollaboratorInput('');

    if (debt?.id) {
      try {
        const res = await axios.put(`${API}/debts/${debt.id}`, {
          collaborators: newCollaborators
        }, { headers: { Authorization: `Bearer ${token}` } });
        onSaved?.(res.data);
        showToast.success(`Colaborador ${email} agregado e invitado ✓`);
      } catch {
        showToast.error('Error al agregar colaborador');
      }
    }
  };

  const confirmRemoveCollaborator = async () => {
    if (!collaboratorToRemove) return;
    const emailToRemove = collaboratorToRemove;
    const newCollaborators = form.collaborators.filter(c => c !== emailToRemove);
    setForm(f => ({ ...f, collaborators: newCollaborators }));
    setCollaboratorToRemove(null);

    if (debt?.id) {
      try {
        const res = await axios.put(`${API}/debts/${debt.id}`, {
          collaborators: newCollaborators
        }, { headers: { Authorization: `Bearer ${token}` } });
        onSaved?.(res.data);
        showToast.success(`Colaborador ${emailToRemove} removido y notificado`);
      } catch {
        showToast.error('Error al remover colaborador');
      }
    }
  };

  const handleInstallmentAmountChange = (val) => {
    setForm(f => {
      const cuota = parseFloat(val);
      const total = parseFloat(f.total_amount);
      const numPagos = (cuota > 0 && total > 0) ? Math.ceil(total / cuota) : '';
      return { ...f, installment_amount: val, total_installments: numPagos ? String(numPagos) : f.total_installments };
    });
  };

  const handleInstallmentsChange = (val) => {
    setForm(f => {
      const num = parseInt(val);
      const total = parseFloat(f.total_amount);
      const calculated = (num > 0 && total > 0) ? (total / num).toFixed(2) : f.installment_amount;
      return { ...f, total_installments: val, installment_amount: calculated };
    });
  };

  const handleTotalAmountChange = (val) => {
    setForm(f => {
      const total = parseFloat(val);
      const cuota = parseFloat(f.installment_amount);
      const num = (cuota > 0 && total > 0) ? Math.ceil(total / cuota) : f.total_installments;
      return { ...f, total_amount: val, total_installments: num ? String(num) : f.total_installments };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.total_amount) return;
    if (form.has_interest && (!form.interest_rate || parseFloat(form.interest_rate) <= 0)) {
      showToast.error('Ingresa una tasa de interés mensual válida');
      return;
    }
    if (form.has_interest && !form.start_date) {
      showToast.error('La fecha de inicio del préstamo es obligatoria para calcular intereses');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        creditor_type: form.creditor_type,
        debt_direction: form.debt_direction,
        creditor_email: form.creditor_email.trim() ? form.creditor_email.trim() : null,
        collaborators: form.collaborators,
        total_amount: parseFloat(form.total_amount),
        currency: form.currency,
        description: form.description.trim() ? form.description.trim() : null,
        color: form.color,
        payment_frequency: form.payment_frequency,
        total_installments: form.total_installments ? parseInt(form.total_installments) : null,
        installment_amount: form.installment_amount ? parseFloat(form.installment_amount) : null,
        start_date: form.start_date || todayISO(),
        due_date: form.due_date ? form.due_date : null,
        auto_reminder: form.auto_reminder,
        has_interest: form.has_interest,
        interest_rate: form.has_interest && form.interest_rate ? parseFloat(form.interest_rate) : null,
      };
      const res = debt?.id
        ? await axios.put(`${API}/debts/${debt.id}`, payload, { headers: { Authorization: `Bearer ${token}` } })
        : await axios.post(`${API}/debts`, payload, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(res.data);
      onClose();
      showToast.success(debt?.id ? 'Deuda actualizada' : 'Deuda creada exitosamente');
    } catch (err) {
      showToast.error(err?.response?.data?.detail || 'Error al guardar deuda');
    } finally {
      setLoading(false);
    }
  };

  const totalNum = parseFloat(form.total_amount) || 0;
  const rateNum = parseFloat(form.interest_rate) || 0;
  const monthlyInt = (totalNum * rateNum) / 100;
  const dailyInt = monthlyInt / 30;

  return (
    <>
      <BottomSheetModal
        onClose={onClose}
        title={debt?.id ? 'Editar Deuda' : 'Nueva Deuda'}
        subtitle="Configura el monto, intereses, colaboradores y plan"
      >
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Selector: ¿Es deuda mía o me deben? */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
              Tipo de Registro *
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, debt_direction: 'i_owe' }))}
                className={`py-2.5 px-3 rounded-2xl text-xs font-bold border transition-all ${
                  form.debt_direction !== 'they_owe_me'
                    ? 'bg-rose-600 text-white border-rose-600 shadow-sm shadow-rose-500/20'
                    : 'bg-white border-slate-200 text-slate-700'
                }`}
              >
                🔴 Debo (Mi Deuda)
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, debt_direction: 'they_owe_me' }))}
                className={`py-2.5 px-3 rounded-2xl text-xs font-bold border transition-all ${
                  form.debt_direction === 'they_owe_me'
                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm shadow-emerald-500/20'
                    : 'bg-white border-slate-200 text-slate-700'
                }`}
              >
                🟢 Me deben (Por cobrar)
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
              {form.debt_direction === 'they_owe_me' ? '¿Quién te debe? / Concepto *' : 'Nombre / Acreedor *'}
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 font-medium"
              placeholder={form.debt_direction === 'they_owe_me' ? 'Ej: Juan Pérez, Carlos' : 'Ej: Préstamo Juan, Tarjeta BBVA...'}
              required
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Tipo de Acreedor</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(CREDITOR_ICONS).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, creditor_type: k }))}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                    form.creditor_type === k
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20 scale-[1.02]'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <v.icon className="w-3.5 h-3.5" />
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
              {form.debt_direction === 'they_owe_me' ? 'Email de quien te debe (opcional)' : 'Email del Acreedor (opcional)'}
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                value={form.creditor_email}
                onChange={e => setForm(f => ({ ...f, creditor_email: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none text-xs"
                placeholder="correo@ejemplo.com (para recordatorios si no tiene cuenta o recibos)"
              />
            </div>
          </div>

          {/* Sección de Colaboradores */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 space-y-2.5">
            <div className="flex items-center gap-1.5">
              <Users className="w-4 h-4 text-indigo-600" />
              <label className="text-xs font-bold text-slate-700">Compartir con Colaboradores</label>
            </div>
            <p className="text-[11px] text-slate-500">
              Agrega el correo de tu pareja, amigo o socio. Ambos verán la deuda y podrán registrar abonos desde sus cuentas.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                value={collaboratorInput}
                onChange={e => setCollaboratorInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddCollaborator(e); }}
                className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl focus:outline-none text-xs"
                placeholder="correo@colaborador.com"
              />
              <button
                type="button"
                onClick={handleAddCollaborator}
                className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all"
              >
                + Agregar
              </button>
            </div>

            {form.collaborators.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {form.collaborators.map(c => (
                  <span key={c} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-indigo-200 text-indigo-900 rounded-full text-[11px] font-medium">
                    {c}
                    <button type="button" onClick={() => setCollaboratorToRemove(c)} className="text-slate-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Monto y Moneda */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Monto Inicial del Préstamo *</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.total_amount ? formatCurrencyInput(form.total_amount) : ''}
                  onChange={e => handleTotalAmountChange(parseCurrency(e.target.value))}
                  className="w-full pl-8 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none font-black text-slate-900 text-base"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Moneda</label>
              <select
                value={form.currency}
                onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                className="w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none font-bold text-slate-700 text-sm"
              >
                {['MXN','USD','EUR','COP','ARS','CLP'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Fecha de Inicio / Desembolso */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
              Fecha de Inicio / Desembolso del Préstamo *
            </label>
            <div className="relative">
              <input
                type="date"
                value={form.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none text-sm font-medium"
                required
              />
            </div>
          </div>

          {/* Fecha Límite Opcional y Recordatorio Automático */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
            <div>
              <label className="text-xs font-bold text-slate-700 block mb-1.5 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                Fecha Límite / Vencimiento (opcional)
              </label>
              <input
                type="date"
                value={form.due_date}
                onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none text-xs font-medium"
              />
            </div>

            <label className="flex items-start gap-2.5 text-xs font-bold text-slate-800 cursor-pointer pt-1 select-none">
              <input
                type="checkbox"
                checked={form.auto_reminder}
                onChange={e => setForm(f => ({ ...f, auto_reminder: e.target.checked }))}
                className="mt-0.5 w-4 h-4 text-indigo-600 rounded cursor-pointer focus:ring-indigo-500"
              />
              <div className="min-w-0">
                <span>Crear recordatorio de {form.debt_direction === 'they_owe_me' ? 'cobro' : 'pago'} en Recordatorios de Pago</span>
                <p className="text-[10px] font-normal text-slate-500">
                  {form.creditor_email
                    ? `Notificará a tu cuenta y enviará recordatorio por correo a ${form.creditor_email}.`
                    : 'Te avisará antes de la fecha límite y si el deudor/acreedor vincula la deuda, se le creará en su cuenta.'}
                </p>
              </div>
            </label>
          </div>

          {/* Sección de Interés Mensual sobre Saldo Insoluto */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-sm">
                  <Percent className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-black text-amber-950 uppercase tracking-wider">Interés Mensual</h4>
                  <p className="text-[11px] text-amber-800">Cálculo exacto sobre saldo insoluto</p>
                </div>
              </div>

              {/* Switch Toggle */}
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.has_interest}
                  onChange={e => setForm(f => ({ ...f, has_interest: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
              </label>
            </div>

            {form.has_interest && (
              <div className="space-y-3 pt-2 border-t border-amber-200/60 animate-fade-in">
                <div>
                  <label className="text-[11px] font-bold text-amber-900 mb-1 block">Tasa de Interés Mensual (%) *</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={form.interest_rate}
                      onChange={e => setForm(f => ({ ...f, interest_rate: e.target.value }))}
                      className="w-full pl-4 pr-9 py-2.5 bg-white border border-amber-200 rounded-xl focus:outline-none font-black text-amber-950 text-sm"
                      placeholder="Ej: 3.0"
                      required={form.has_interest}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-600 font-bold text-sm">%</span>
                  </div>
                </div>

                {totalNum > 0 && rateNum > 0 && (
                  <div className="bg-white/80 rounded-xl p-3 border border-amber-200 text-xs text-amber-950 space-y-1">
                    <p className="font-bold flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                      Interés inicial: {formatCurrency(monthlyInt)} / mes ({formatCurrency(dailyInt)} / día aprox.)
                    </p>
                    <p className="text-[10.5px] text-amber-800 leading-relaxed">
                      Al abonar, puedes indicar <b>cuánto es a Capital</b> y <b>cuánto a Interés</b> directamente en cada pago.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Plan de Pagos Opcional */}
          <div className="bg-indigo-50/70 border border-indigo-100 rounded-2xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Calculator className="w-4 h-4 text-indigo-600" />
              <h4 className="text-xs font-black text-indigo-950 uppercase tracking-wider">Plan de Pagos / Cuotas</h4>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-500 mb-1.5 block">¿Cada cuánto pagarás?</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(FREQUENCY_LABELS).map(([k, l]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, payment_frequency: k }))}
                    className={`py-2 px-3 rounded-xl text-xs font-bold transition-all text-left truncate ${
                      form.payment_frequency === k
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'bg-white text-slate-600 border border-slate-200'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {form.payment_frequency !== 'none' && (
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="text-[11px] font-bold text-slate-500 mb-1 block">¿Cuánto por cuota?</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={form.installment_amount ? formatCurrencyInput(form.installment_amount) : ''}
                      onChange={e => handleInstallmentAmountChange(parseCurrency(e.target.value))}
                      className="w-full pl-7 pr-3 py-2 bg-white border border-slate-200 rounded-xl focus:outline-none text-xs font-bold text-slate-800"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-bold text-slate-500 mb-1 block">Total de pagos</label>
                  <input
                    type="number"
                    min="1"
                    value={form.total_installments}
                    onChange={e => handleInstallmentsChange(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl focus:outline-none text-xs font-bold text-slate-800"
                    placeholder="Calculado..."
                  />
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Descripción / Motivo</label>
            <input
              type="text"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none text-xs"
              placeholder="Detalle o acuerdo pactado (opcional)"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Color distintivo</label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, color: c }))}
                  style={{ backgroundColor: c }}
                  className={`w-7 h-7 rounded-full transition-all flex items-center justify-center ${
                    form.color === c ? 'ring-2 ring-offset-2 ring-slate-800 scale-110' : ''
                  }`}
                >
                  {form.color === c && <Check className="w-3.5 h-3.5 text-white" />}
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-2xl shadow-lg active:scale-95 transition-all disabled:opacity-60"
          >
            {loading ? 'Guardando...' : (debt?.id ? 'Actualizar Deuda' : 'Crear Deuda')}
          </button>
        </form>
      </BottomSheetModal>

      {collaboratorToRemove && (
        <ConfirmDeleteModal
          title="¿Remover colaborador?"
          message={`¿Seguro que deseas remover a ${collaboratorToRemove} de esta deuda compartida? Se le enviará una notificación por correo y campanita.`}
          onConfirm={confirmRemoveCollaborator}
          onCancel={() => setCollaboratorToRemove(null)}
        />
      )}
    </>
  );
};

// ─── Movimiento con Swipe, Edición y Desglose de Interés / Capital ─────────
const SwipeableMovement = ({ movement: m, allMovements = [], hasInterest, items = [], onEdit, onDelete, onOpenReceipt, isOpen, onOpen, onClose, isFirstItem, isReadOnly = false, canVerify = false, onVerify, isVerifying = false }) => {
  const startX = useRef(null);
  const [offsetX, setOffsetX] = useState(0);
  const isHinting = useRef(false);

  useEffect(() => {
    if (!isReadOnly && isFirstItem && !isHinting.current) {
      isHinting.current = true;
      const t1 = setTimeout(() => setOffsetX(-28), 700);
      const t2 = setTimeout(() => setOffsetX(0), 1500);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [isFirstItem, isReadOnly]);

  useEffect(() => {
    if (!isOpen && offsetX !== 0) {
      setOffsetX(0);
    }
  }, [isOpen]);

  const onTouchStart = e => {
    if (isReadOnly) return;
    startX.current = e.touches[0].clientX;
  };

  const onTouchMove = e => {
    if (isReadOnly || startX.current === null) return;
    const dx = e.touches[0].clientX - startX.current;
    if (dx < 0) {
      setOffsetX(Math.max(dx, -135));
    }
  };

  const onTouchEnd = () => {
    if (isReadOnly) return;
    if (offsetX < -45) {
      setOffsetX(-125);
      if (onOpen) onOpen();
    } else {
      setOffsetX(0);
      if (onClose) onClose();
    }
    startX.current = null;
  };

  return (
    <div className="relative overflow-hidden rounded-2xl">
      {/* Botones de acción detrás: Editar y Borrar */}
      {!isReadOnly && (
        <div className={`absolute right-0 top-0 bottom-0 w-32 flex items-center justify-end rounded-r-2xl transition-opacity duration-200 ${offsetX < 0 ? 'opacity-100' : 'opacity-0'}`}>
          <button
            onClick={() => { setOffsetX(0); onClose?.(); onEdit?.(m); }}
            className="w-16 h-full bg-indigo-600 flex flex-col items-center justify-center gap-1 text-white active:scale-95 transition-transform"
          >
            <Edit3 className="w-4 h-4" />
            <span className="text-[9px] font-bold">Editar</span>
          </button>
          <button
            onClick={() => { setOffsetX(0); onClose?.(); onDelete(m.id); }}
            className="w-16 h-full bg-rose-500 rounded-r-2xl flex flex-col items-center justify-center gap-1 text-white active:scale-95 transition-transform"
          >
            <Trash2 className="w-4 h-4" />
            <span className="text-[9px] font-bold">Borrar</span>
          </button>
        </div>
      )}

      <div
        style={{
          transform: `translate3d(${offsetX}px, 0, 0)`,
          transition: startX.current ? 'none' : 'transform 0.24s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={() => {
          if (isOpen) {
            setOffsetX(0);
            onClose?.();
          }
        }}
        className="bg-white border border-slate-100 p-4 flex items-center gap-3 rounded-2xl relative select-none will-change-transform"
      >
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
          m.is_extra
            ? 'bg-amber-50'
            : (m.type === 'payment' ? 'bg-emerald-50' : 'bg-rose-50')
        }`}>
          {m.is_extra ? (
            <Zap className="w-5 h-5 text-amber-500" />
          ) : (
            m.type === 'payment' ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <AlertCircle className="w-5 h-5 text-rose-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-bold text-sm text-slate-900 truncate">{m.description}</p>
            {m.is_extra && (
              <span className="text-[9px] px-1.5 py-0.2 bg-amber-100 text-amber-700 rounded-full font-bold shrink-0">
                ⚡ Extra
              </span>
            )}
            {m.debt_item_ids && Array.isArray(m.debt_item_ids) && m.debt_item_ids.length > 0 ? (
              m.debt_item_ids.map(id => {
                const matched = (items || []).find(it => it.id === id);
                if (!matched) return null;
                return (
                  <span
                    key={id}
                    className="text-[9px] px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full font-bold inline-flex items-center gap-1 shrink-0 whitespace-nowrap"
                  >
                    <span className="shrink-0">🎯</span>
                    <span>{matched.title}</span>
                  </span>
                );
              })
            ) : m.debt_item_id ? (() => {
              const matched = (items || []).find(it => it.id === m.debt_item_id);
              return matched ? (
                <span
                  className="text-[9px] px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full font-bold inline-flex items-center gap-1 shrink-0 whitespace-nowrap"
                >
                  <span className="shrink-0">🎯</span>
                  <span>{matched.title}</span>
                </span>
              ) : null;
            })() : (
              items && items.length > 0 && m.type === 'payment' ? (() => {
                const directPaid = {};
                (allMovements || []).forEach(mov => {
                  if (mov.type === 'payment') {
                    if (mov.debt_item_ids && Array.isArray(mov.debt_item_ids) && mov.debt_item_ids.length > 0) {
                      const split = (mov.amount || 0) / mov.debt_item_ids.length;
                      mov.debt_item_ids.forEach(id => { directPaid[id] = (directPaid[id] || 0) + split; });
                    } else if (mov.debt_item_id) {
                      directPaid[mov.debt_item_id] = (directPaid[mov.debt_item_id] || 0) + (mov.amount || 0);
                    }
                  }
                });
                const itemRemaining = {};
                items.forEach(it => { itemRemaining[it.id] = Math.max(0, (it.amount || 0) - (directPaid[it.id] || 0)); });
                const genericPayments = (allMovements || []).filter(mov => mov.type === 'payment' && !mov.debt_item_id && (!mov.debt_item_ids || mov.debt_item_ids.length === 0));
                let targetConcept = null;
                for (const gm of genericPayments) {
                  let rem = gm.amount || 0;
                  let cTitle = null;
                  for (const it of items) {
                    if (rem <= 0) break;
                    if (itemRemaining[it.id] > 0) {
                      const alloc = Math.min(rem, itemRemaining[it.id]);
                      itemRemaining[it.id] -= alloc;
                      rem -= alloc;
                      if (!cTitle) cTitle = it.title;
                    }
                  }
                  if (gm.id === m.id) {
                    targetConcept = cTitle || (items[0] ? items[0].title : null);
                    break;
                  }
                }
                const finalTarget = targetConcept || (items[0] ? items[0].title : null);
                return (
                  <span
                    className="text-[9px] px-2 py-0.5 bg-slate-100 text-slate-700 border border-slate-200 rounded-full font-bold inline-flex items-center gap-1 shrink-0 whitespace-nowrap"
                    title={finalTarget ? `Abono general aplicado a: ${finalTarget}` : 'Abono general'}
                  >
                    <span className="shrink-0">🌐</span>
                    <span>{finalTarget ? `General → ${finalTarget}` : 'Abono general'}</span>
                  </span>
                );
              })() : null
            )}
            {m.receipt_url && (
              <button
                onClick={(e) => { e.stopPropagation(); onOpenReceipt(m.receipt_url); }}
                className="text-[9px] px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full font-bold flex items-center gap-1 hover:bg-indigo-100 transition-all shrink-0"
              >
                <Paperclip className="w-2.5 h-2.5" />
                Comprobante
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <p className="text-[11px] text-slate-400 font-medium">{m.date?.split('T')[0]}</p>
            {hasInterest && m.type === 'payment' && (m.interest_covered !== undefined && m.principal_covered !== undefined) && (
              <span className="text-[10px] text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full font-medium">
                Capital: <b className="text-emerald-700">{formatCurrency(m.principal_covered || 0)}</b> | Interés: <b className="text-amber-700">{formatCurrency(m.interest_covered || 0)}</b>
              </span>
            )}
            {m.verified_by ? (
              <span className="text-[9px] px-1.5 py-0.2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full font-bold flex items-center gap-0.5">
                <ShieldCheck className="w-2.5 h-2.5" /> Validado por {m.verified_by}
              </span>
            ) : (
              canVerify && m.type === 'payment' && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onVerify?.(m); }}
                  disabled={isVerifying}
                  className="text-[10px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-bold rounded-lg shadow-sm flex items-center gap-1 transition-all disabled:opacity-50"
                  title="Dar visto bueno a este pago"
                >
                  <ShieldCheck className="w-3 h-3" />
                  {isVerifying ? 'Validando...' : '✓ Dar visto bueno'}
                </button>
              )
            )}
          </div>
        </div>
        <div className="text-right shrink-0 flex items-center gap-2">
          <p className={`font-black text-sm ${m.type === 'payment' ? (m.is_extra ? 'text-amber-600' : 'text-emerald-600') : 'text-rose-600'}`}>
            {m.type === 'payment' ? '-' : '+'}{formatCurrency(m.amount)}
          </p>
          <div className="hidden sm:flex items-center gap-1 ml-1">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit?.(m); }}
              className="w-7 h-7 rounded-lg bg-slate-50 items-center justify-center text-slate-400 hover:text-indigo-600 transition-colors"
              title="Editar"
            >
              <Edit3 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(m.id); }}
              className="w-7 h-7 rounded-lg bg-slate-50 items-center justify-center text-slate-400 hover:text-red-500 transition-colors"
              title="Borrar"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Concepto del Desglose con Swipe & Edición ─────────
const SwipeableItem = ({ item, covered, isCovered, isPartial, pct, onEdit, onDelete, isOpen, onOpen, onClose, isFirstItem, isReadOnly = false }) => {
  const startX = useRef(null);
  const [offsetX, setOffsetX] = useState(0);
  const isHinting = useRef(false);

  useEffect(() => {
    if (isFirstItem && !isHinting.current && !isReadOnly) {
      isHinting.current = true;
      const t1 = setTimeout(() => setOffsetX(-28), 700);
      const t2 = setTimeout(() => setOffsetX(0), 1500);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [isFirstItem, isReadOnly]);

  useEffect(() => {
    if (!isOpen && offsetX !== 0) {
      setOffsetX(0);
    }
  }, [isOpen]);

  const onTouchStart = e => {
    if (isReadOnly) return;
    startX.current = e.touches[0].clientX;
  };

  const onTouchMove = e => {
    if (isReadOnly || startX.current === null) return;
    const dx = e.touches[0].clientX - startX.current;
    if (dx < 0) {
      setOffsetX(Math.max(dx, -135));
    }
  };

  const onTouchEnd = () => {
    if (isReadOnly) return;
    if (offsetX < -45) {
      setOffsetX(-125);
      if (onOpen) onOpen();
    } else {
      setOffsetX(0);
      if (onClose) onClose();
    }
    startX.current = null;
  };

  return (
    <div className="relative overflow-hidden rounded-2xl">
      {/* Botones de acción detrás: Editar y Borrar (solo si no es de solo lectura) */}
      {!isReadOnly && (
        <div className={`absolute right-0 top-0 bottom-0 w-32 flex items-center justify-end rounded-r-2xl transition-opacity duration-200 ${offsetX < 0 ? 'opacity-100' : 'opacity-0'}`}>
          <button
            onClick={() => { setOffsetX(0); onClose?.(); onEdit(item); }}
            className="w-16 h-full bg-indigo-600 flex flex-col items-center justify-center gap-1 text-white active:scale-95 transition-transform"
          >
            <Edit3 className="w-4 h-4" />
            <span className="text-[9px] font-bold">Editar</span>
          </button>
          <button
            onClick={() => { setOffsetX(0); onClose?.(); onDelete(item.id); }}
            className="w-16 h-full bg-rose-500 rounded-r-2xl flex flex-col items-center justify-center gap-1 text-white active:scale-95 transition-transform"
          >
            <Trash2 className="w-4 h-4" />
            <span className="text-[9px] font-bold">Borrar</span>
          </button>
        </div>
      )}

      <div
        style={{
          transform: `translate3d(${offsetX}px, 0, 0)`,
          transition: startX.current ? 'none' : 'transform 0.24s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={() => {
          if (isOpen && !isReadOnly) {
            setOffsetX(0);
            onClose?.();
          }
        }}
        className={`bg-white border rounded-2xl p-4 transition-all will-change-transform select-none ${
          isCovered ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-100'
        }`}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <p className={`font-bold text-sm truncate ${isCovered ? 'text-emerald-950 font-black' : 'text-slate-900'}`}>
                {item.title}
              </p>
              {isCovered && (
                <span className="text-[10px] px-2.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-bold whitespace-nowrap shrink-0">
                  ✓ Liquidado
                </span>
              )}
              {isPartial && (
                <span className="text-[10px] px-2.5 py-0.5 bg-blue-100 text-blue-800 rounded-full font-bold whitespace-nowrap shrink-0">
                  {pct.toFixed(0)}% cubierto
                </span>
              )}
              {!isCovered && !isPartial && (
                <span className="text-[10px] px-2.5 py-0.5 bg-slate-100 text-slate-500 rounded-full font-medium whitespace-nowrap shrink-0">
                  Pendiente
                </span>
              )}
            </div>
            {item.notes && <p className="text-[10px] text-slate-400 truncate">{item.notes}</p>}
          </div>
          <div className="text-right shrink-0 flex items-start gap-2">
            <div>
              <p className={`font-black text-sm ${isCovered ? 'text-emerald-600' : 'text-slate-900'}`}>
                {formatCurrency(item.amount)}
              </p>
              {isPartial && (
                <p className="text-[10px] text-blue-600 font-bold">
                  Abonado: {formatCurrency(covered)}
                </p>
              )}
            </div>
            {!isReadOnly && (
              <div className="hidden sm:flex items-center gap-1 ml-1">
                <button
                  onClick={(e) => { e.stopPropagation(); onEdit(item); }}
                  className="w-7 h-7 rounded-lg bg-slate-50 items-center justify-center text-slate-400 hover:text-indigo-600 transition-colors"
                  title="Editar"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
                  className="w-7 h-7 rounded-lg bg-slate-50 items-center justify-center text-slate-400 hover:text-red-500 transition-colors"
                  title="Borrar"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Barra de progreso */}
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${
              isCovered ? 'bg-emerald-500' : (isPartial ? 'bg-blue-500' : 'bg-transparent')
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Vista Detalle de una Deuda ───
const DebtDetail = ({ debt: initial, onBack, onDeleted }) => {
  const { token, user } = useContext(AuthContext);
  const [debt, setDebt] = useState(initial);
  const [activeTab, setActiveTab] = useState('payments'); // 'payments' | 'breakdown'
  const [movements, setMovements] = useState([]);
  const [items, setItems] = useState([]);
  const [comments, setComments] = useState([]);
  const [activeSwipeId, setActiveSwipeId] = useState(null);
  const [showMovModal, setShowMovModal] = useState(false);
  const [editingMovement, setEditingMovement] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSendReceiptModal, setShowSendReceiptModal] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmVerifyMovement, setConfirmVerifyMovement] = useState(null);
  const [verifyingId, setVerifyingId] = useState(null);
  const [loadingShare, setLoadingShare] = useState(false);
  const inactivityTimer = useRef(null);
  const frontendUrl = window.location.origin;

  useEffect(() => { fetchAll(); }, [debt.id]);

  const handleItemOpen = (id) => {
    setActiveSwipeId(id);
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      setActiveSwipeId(null);
    }, 4500);
  };

  const handleGlobalClick = () => {
    if (activeSwipeId) setActiveSwipeId(null);
  };

  const fetchAll = async () => {
    try {
      const [movRes, itemsRes, debtsRes] = await Promise.all([
        axios.get(`${API}/debts/${debt.id}/movements`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/debts/${debt.id}/items`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/debts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setMovements(movRes.data);
      setItems(itemsRes.data);
      const updated = debtsRes.data.find(d => d.id === debt.id);
      if (updated) setDebt(updated);
      if (updated?.share_token) {
        axios.get(`${API}/public/debt/${updated.share_token}/comments`)
          .then(r => setComments(r.data))
          .catch(() => {});
      }
    } catch {
      showToast.error('Error al cargar datos');
    }
  };

  const executeDelete = async () => {
    if (!confirmDelete) return;
    const { type, id } = confirmDelete;
    try {
      if (type === 'movement') {
        await axios.delete(`${API}/debts/${debt.id}/movements/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        showToast.success('Movimiento eliminado');
        fetchAll();
      } else if (type === 'item') {
        await axios.delete(`${API}/debts/${debt.id}/items/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        showToast.success('Concepto eliminado');
        fetchAll();
      } else if (type === 'debt') {
        await axios.delete(`${API}/debts/${debt.id}`, { headers: { Authorization: `Bearer ${token}` } });
        window.dispatchEvent(new CustomEvent('transaction-added'));
        onDeleted(debt.id);
      }
    } catch {
      showToast.error('Error al eliminar');
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleVerifyMovement = async (mov) => {
    if (!mov) return;
    setVerifyingId(mov.id);
    try {
      const verifierName = user?.name || user?.username || 'Acreedor';
      await axios.post(
        `${API}/debts/${debt.id}/movements/${mov.id}/verify`,
        { verifier_name: verifierName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success('✓ Pago validado de conformidad');
      setConfirmVerifyMovement(null);
      fetchAll();
      window.dispatchEvent(new CustomEvent('transaction-added'));
    } catch (err) {
      showToast.error(err?.response?.data?.detail || 'Error al validar el pago');
    } finally {
      setVerifyingId(null);
    }
  };

  const handleShare = async () => {
    setLoadingShare(true);
    try {
      const res = await axios.post(`${API}/debts/${debt.id}/share`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const newUrl = `${frontendUrl}/public/debt/${res.data.share_token}`;
      setDebt(d => ({ ...d, share_token: res.data.share_token }));
      navigator.clipboard?.writeText(newUrl);
      showToast.success('Link copiado al portapapeles');
    } catch {
      showToast.error('Error al generar link');
    } finally {
      setLoadingShare(false);
    }
  };

  const handleRevokeShare = async () => {
    if (!window.confirm('¿Revocar el link compartido?')) return;
    try {
      await axios.delete(`${API}/debts/${debt.id}/share`, { headers: { Authorization: `Bearer ${token}` } });
      setDebt(d => ({ ...d, share_token: null }));
      showToast.success('Link revocado');
    } catch {
      showToast.error('Error al revocar');
    }
  };

  const [loadingToggle, setLoadingToggle] = useState(false);

  const handleToggleDirection = async () => {
    setLoadingToggle(true);
    try {
      const res = await axios.post(`${API}/debts/${debt.id}/toggle-direction`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDebt(res.data);
      const isNowMeDeben = res.data.debt_direction === 'they_owe_me';
      showToast.success(`Movida a "${isNowMeDeben ? 'Me deben' : 'Debo'}". Sincronizada con usuario vinculado.`);
      fetchAll();
      window.dispatchEvent(new CustomEvent('transaction-added'));
    } catch (err) {
      showToast.error(err?.response?.data?.detail || 'Error al cambiar dirección de la deuda');
    } finally {
      setLoadingToggle(false);
    }
  };

  const totalCharges = movements.filter(m => m.type === 'charge').reduce((s, m) => s + m.amount, 0);
  const totalPaid = movements.filter(m => m.type === 'payment').reduce((s, m) => s + m.amount, 0);
  const totalInterestPaid = debt.total_interest_paid || movements.reduce((s, m) => s + (m.interest_covered || 0), 0);
  const totalPrincipalPaid = totalPaid - totalInterestPaid;
  const initialPrincipal = debt.initial_amount || debt.total_amount || 0;
  const currentPrincipal = debt.current_principal !== undefined ? debt.current_principal : Math.max(0, initialPrincipal + totalCharges - totalPrincipalPaid);
  const totalEffectiveToLiquidate = debt.current_balance;

  const effectiveTotal = Math.max(
    debt.total_amount || 0,
    initialPrincipal + totalCharges + (debt.total_interest_generated || 0),
    (debt.current_balance || 0) + totalPaid
  );
  const progress = effectiveTotal > 0 ? Math.min(Math.max((totalPaid / effectiveTotal) * 100, 0), 100) : 0;
  const statusCfg = STATUS_CONFIG[debt.status] || STATUS_CONFIG.active;
  const CreditorIcon = CREDITOR_ICONS[debt.creditor_type]?.icon || CreditCard;
  const shareUrl = debt.share_token ? `${frontendUrl}/public/debt/${debt.share_token}` : null;
  const totalExtra = movements.filter(m => m.is_extra).reduce((s, m) => s + m.amount, 0);
  const regularPaymentsCount = movements.filter(m => m.type === 'payment' && !m.is_extra).length;
  const totalItemsSum = items.reduce((s, i) => s + i.amount, 0);
  const isFullyBreakdown = totalItemsSum >= (debt.total_amount || 0);
  const isCreator = debt.is_creator !== false && !debt.is_mirror && !String(debt.description || '').startsWith('Deuda vinculada automáticamente desde PGastos');
  const isLinked = Boolean(debt.linked_user_id || debt.is_mirror || (debt.share_token && !isCreator));
  const isCreditorInLinkedDebt = isLinked && debt.debt_direction === 'they_owe_me';

  return (
    <Layout>
      <div onClick={handleGlobalClick} className="max-w-4xl mx-auto px-4 py-4 pb-28">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-slate-400 font-medium mb-4">
          <button onClick={onBack} className="hover:text-indigo-600 flex items-center gap-1 font-bold">
            <ArrowLeft className="w-3.5 h-3.5" /> Deudas
          </button>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-slate-700 font-bold truncate">{debt.name}</span>
        </div>

        {/* Header color */}
        <div className="relative px-6 py-6 rounded-3xl mb-6 text-white shadow-xl" style={{ background: `linear-gradient(135deg,${debt.color}ee,${debt.color}99)` }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur">
                <CreditorIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-white">{debt.name}</h1>
                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                  <span className="text-white/80 text-xs font-medium">{CREDITOR_ICONS[debt.creditor_type]?.label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${statusCfg.color}`}>{statusCfg.label}</span>
                  {debt.has_interest && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-400 text-slate-950 flex items-center gap-1">
                      <Percent className="w-3 h-3" /> {debt.interest_rate}% mensual
                    </span>
                  )}
                  {debt.collaborators?.length > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white flex items-center gap-1">
                      <Users className="w-3 h-3" /> {debt.collaborators.length} colab.
                    </span>
                  )}
                  {debt.agreement_status && (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${debt.agreement_status === 'conforme' ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'}`}>
                      {debt.agreement_status === 'conforme' ? '✓ Conforme' : '⚠️ Aclaración'}
                    </span>
                  )}
                  {isCreator && (
                    <button
                      type="button"
                      onClick={handleToggleDirection}
                      disabled={loadingToggle}
                      title="Alternar entre 'Me deben' y 'Debo'"
                      className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/20 hover:bg-white/30 text-white flex items-center gap-1 transition-all active:scale-95 cursor-pointer shadow-sm disabled:opacity-50"
                    >
                      <ArrowLeftRight className="w-2.5 h-2.5" />
                      <span>{debt.debt_direction === 'they_owe_me' ? '🟢 Me deben' : '🔴 Debo'} (Cambiar)</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowEditModal(true)} className="w-9 h-9 bg-white/20 hover:bg-white/30 backdrop-blur rounded-full flex items-center justify-center transition-all">
                <Edit3 className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={() => setConfirmDelete({
                  type: 'debt',
                  id: debt.id,
                  title: '¿Eliminar esta deuda?',
                  message: 'Se borrarán todos los pagos, comprobantes y conceptos asociados.'
                })}
                className="w-9 h-9 bg-white/20 hover:bg-white/30 backdrop-blur rounded-full flex items-center justify-center transition-all"
              >
                <Trash2 className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {/* Resumen numérico */}
          <div className="bg-white/15 backdrop-blur rounded-2xl p-4 sm:p-5">
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="text-center">
                <p className="text-white/70 text-[10px] sm:text-xs font-bold uppercase">
                  {debt.has_interest ? 'Total Deuda' : 'Monto Total'}
                </p>
                <p className="text-white font-black text-sm sm:text-lg">{formatCurrency(initialPrincipal + totalCharges)}</p>
              </div>
              <div className="text-center border-x border-white/20">
                <p className="text-white/70 text-[10px] sm:text-xs font-bold uppercase">Total Pagado</p>
                <p className="text-emerald-300 font-black text-sm sm:text-lg">{formatCurrency(totalPaid)}</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-[10px] sm:text-xs font-bold uppercase">
                  {debt.has_interest ? 'Total para Liquidar' : 'Saldo Restante'}
                </p>
                <p className="text-white font-black text-sm sm:text-lg">{formatCurrency(totalEffectiveToLiquidate)}</p>
              </div>
            </div>
            <div className="h-2.5 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-white/80 text-xs text-right mt-1.5 font-bold">{progress.toFixed(0)}% pagado</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Columna Izquierda: Tarjeta de Intereses, Plan de Pagos & Acciones */}
          <div className="space-y-4">
            {/* Tarjeta Detallada de Intereses (si genera interés mensual) */}
            {debt.has_interest && (
              <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-3xl p-5 text-white shadow-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
                      <Percent className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 className="font-black text-sm text-white">Interés sobre Saldo</h3>
                      <p className="text-[10px] text-white/80">Tasa del {debt.interest_rate}% mensual</p>
                    </div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/20 font-bold">Saldo Insoluto</span>
                </div>

                <div className="grid grid-cols-2 gap-2 bg-black/15 backdrop-blur rounded-2xl p-3 text-xs">
                  <div>
                    <p className="text-white/70 text-[10px] font-bold uppercase">Capital Pendiente</p>
                    <p className="font-black text-sm text-white">{formatCurrency(currentPrincipal)}</p>
                  </div>
                  <div>
                    <p className="text-white/70 text-[10px] font-bold uppercase">Interés Pendiente</p>
                    <p className="font-black text-sm text-amber-200">{formatCurrency(debt.accumulated_interest || 0)}</p>
                  </div>
                </div>

                <div className="bg-white/10 rounded-xl p-2.5 text-[11px] text-white/90 space-y-1">
                  <div className="flex justify-between">
                    <span>Capital amortizado:</span>
                    <b className="text-emerald-300">{formatCurrency(totalPrincipalPaid)}</b>
                  </div>
                  <div className="flex justify-between">
                    <span>Intereses pagados:</span>
                    <b className="text-amber-200">{formatCurrency(totalInterestPaid)}</b>
                  </div>
                  <div className="flex justify-between">
                    <span>Fecha de desembolso:</span>
                    <b>{debt.start_date || 'No registrada'}</b>
                  </div>
                </div>
              </div>
            )}

            {/* Plan de Pagos */}
            {debt.payment_frequency && debt.payment_frequency !== 'none' && (
              <div className="bg-white border border-indigo-100 rounded-3xl p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-indigo-600" />
                    <h3 className="font-black text-slate-800 text-sm">Plan {FREQUENCY_LABELS[debt.payment_frequency]}</h3>
                  </div>
                  {debt.start_date && (
                    <span className="text-[10px] text-slate-400 font-medium">Inicio: {debt.start_date}</span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 bg-slate-50 rounded-2xl p-3 mb-2">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Cuota acordada</p>
                    <p className="font-black text-slate-800 text-sm">
                      {debt.installment_amount ? formatCurrency(debt.installment_amount) : 'Libre'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Cuotas realizadas</p>
                    <p className="font-black text-slate-800 text-sm">
                      {regularPaymentsCount} {debt.total_installments ? `de ${debt.total_installments}` : 'pagos'}
                    </p>
                  </div>
                </div>

                {totalExtra > 0 && (
                  <div className="flex items-center justify-between text-xs bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                    <span className="font-bold text-amber-800 flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-amber-500" /> Abonos extras:</span>
                    <span className="font-black text-amber-700">{formatCurrency(totalExtra)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Acciones: Compartir Link & Enviar Recibo & Alternar Dirección */}
            <div className="space-y-3">
              {isCreator ? (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={handleShare}
                    disabled={loadingShare}
                    className="flex items-center justify-center gap-2 py-3.5 bg-white border border-slate-200 rounded-2xl text-xs font-bold text-slate-700 shadow-sm active:scale-95 transition-all disabled:opacity-60 hover:bg-slate-50"
                  >
                    <Share2 className="w-4 h-4 text-indigo-500" />
                    {loadingShare ? 'Generando...' : (shareUrl ? 'Copiar Link' : 'Compartir Link')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSendReceiptModal(true)}
                    className="flex items-center justify-center gap-2 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl text-xs font-bold shadow-md shadow-emerald-600/20 active:scale-95 transition-all"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {debt.status === 'paid' ? 'Enviar Liquidación' : (debt.debt_direction === 'they_owe_me' ? 'Enviar Recibo' : 'Notificar Pago')}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowSendReceiptModal(true)}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl text-xs font-bold shadow-md shadow-emerald-600/20 active:scale-95 transition-all"
                >
                  <MessageCircle className="w-4 h-4" />
                  {debt.status === 'paid' ? 'Enviar Liquidación' : 'Notificar Pago / Enviar Recibo'}
                </button>
              )}

              {/* Botón para alternar pestaña / dirección (solo creador original) */}
              {isCreator && (
                <button
                  type="button"
                  onClick={handleToggleDirection}
                  disabled={loadingToggle}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200/90 rounded-2xl text-xs font-bold text-slate-700 active:scale-95 transition-all disabled:opacity-60 shadow-sm"
                >
                  <ArrowLeftRight className="w-3.5 h-3.5 text-indigo-600" />
                  <span>
                    {debt.debt_direction === 'they_owe_me'
                      ? 'Mover a pestaña "🔴 Debo"'
                      : 'Mover a pestaña "🟢 Me deben"'}
                  </span>
                </button>
              )}

              {isCreator && shareUrl && (
                <div className="bg-indigo-50/70 border border-indigo-200 rounded-2xl p-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Link className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                    <p className="text-[10px] text-indigo-700 font-mono truncate">{shareUrl}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard?.writeText(shareUrl);
                        showToast.success('Link copiado al portapapeles');
                      }}
                      className="text-[10px] px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg transition-all active:scale-95"
                    >
                      Copiar
                    </button>
                    {isCreator && (
                      <button
                        type="button"
                        onClick={handleRevokeShare}
                        className="text-[10px] text-red-500 font-bold hover:underline"
                      >
                        Revocar
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Columna Derecha: Tabs Pagos vs Desglose */}
          <div className="lg:col-span-2 space-y-4">
            {/* TABS */}
            <div className="flex bg-slate-200/70 p-1.5 rounded-2xl">
              <button
                onClick={() => setActiveTab('payments')}
                className={`flex-1 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                  activeTab === 'payments' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Historial de Pagos ({movements.length})</span>
              </button>
              <button
                onClick={() => setActiveTab('breakdown')}
                className={`flex-1 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                  activeTab === 'breakdown' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <ListChecks className="w-3.5 h-3.5" />
                <span>Desglose de Conceptos ({items.length})</span>
              </button>
            </div>

            {/* TAB 1: HISTORIAL DE PAGOS */}
            {activeTab === 'payments' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-black text-slate-800 text-sm">Movimientos y Abonos</h2>
                    {movements.length > 0 && (
                      <p className="text-[10px] text-slate-400 font-medium">← Desliza a la izquierda para editar o borrar</p>
                    )}
                  </div>
                  {!isCreditorInLinkedDebt && (
                    <button
                      onClick={() => { setEditingMovement(null); setShowMovModal(true); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-bold rounded-xl shadow-md active:scale-95 transition-all"
                    >
                      <Plus className="w-3.5 h-3.5" />Registrar Pago
                    </button>
                  )}
                </div>
                {movements.length === 0
                  ? <div className="text-center py-12 bg-white rounded-3xl border border-slate-100 p-6"><TrendingDown className="w-10 h-10 text-slate-200 mx-auto mb-2" /><p className="text-slate-400 text-sm">Sin pagos registrados aún</p></div>
                  : (
                    <div className="space-y-2.5">
                      {movements.map((m, idx) => (
                        <SwipeableMovement
                          key={m.id}
                          movement={m}
                          allMovements={movements}
                          hasInterest={debt.has_interest}
                          items={items}
                          isFirstItem={idx === 0}
                          isReadOnly={isCreditorInLinkedDebt}
                          canVerify={isCreditorInLinkedDebt || (debt.debt_direction === 'they_owe_me' && Boolean(debt.share_token || isLinked))}
                          onVerify={(mov) => setConfirmVerifyMovement(mov)}
                          isVerifying={verifyingId === m.id}
                          isOpen={activeSwipeId === m.id}
                          onOpen={() => handleItemOpen(m.id)}
                          onClose={() => setActiveSwipeId(null)}
                          onEdit={isCreditorInLinkedDebt ? null : (mov) => { setEditingMovement(mov); setShowMovModal(true); }}
                          onDelete={isCreditorInLinkedDebt ? null : () => setConfirmDelete({
                            type: 'movement',
                            id: m.id,
                            title: '¿Eliminar este movimiento?',
                            message: `Se cancelará el registro de ${formatCurrency(m.amount)} (${m.description}).`
                          })}
                          onOpenReceipt={url => setViewingReceipt(url)}
                        />
                      ))}
                    </div>
                  )}
              </div>
            )}

            {/* TAB 2: DESGLOSE DE CONCEPTOS CON CASCADA */}
            {activeTab === 'breakdown' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-black text-slate-800 text-sm">Conceptos de esta Deuda</h2>
                    <p className="text-[11px] text-slate-400">
                      {items.length > 0
                        ? `Desglosado: ${formatCurrency(totalItemsSum)} de ${formatCurrency(debt.total_amount)}`
                        : `Concepto acordado: ${formatCurrency(debt.total_amount)}`}
                    </p>
                  </div>
                  {isCreator && (
                    <button
                      onClick={() => {
                        if (isFullyBreakdown) {
                          showToast.info('La deuda ya está 100% desglosada. Para agregar más conceptos, incrementa el monto total de la deuda.');
                          return;
                        }
                        setEditingItem(null);
                        setShowItemModal(true);
                      }}
                      disabled={isFullyBreakdown}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl shadow-md transition-all ${
                        isFullyBreakdown
                          ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                          : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white active:scale-95'
                      }`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {isFullyBreakdown ? '100% Desglosado' : 'Agregar Concepto'}
                    </button>
                  )}
                </div>

                {/* Resumen de estado del desglose si hay múltiples items */}
                {items.length > 0 && (
                  <div className={`p-3 rounded-2xl text-xs flex items-center justify-between ${
                    totalItemsSum === debt.total_amount
                      ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                      : (totalItemsSum < debt.total_amount
                          ? 'bg-indigo-50 border border-indigo-200 text-indigo-800'
                          : 'bg-amber-50 border border-amber-200 text-amber-800')
                  }`}>
                    <span className="font-medium">
                      {totalItemsSum === debt.total_amount
                        ? '✓ Total 100% desglosado'
                        : (totalItemsSum < debt.total_amount
                            ? `Faltan ${formatCurrency(debt.total_amount - totalItemsSum)} por asignar`
                            : `Excede por ${formatCurrency(totalItemsSum - debt.total_amount)}`)}
                    </span>
                  </div>
                )}

                {items.length === 0 ? (
                  <div className="space-y-3">
                    {/* Tarjeta del concepto principal con el que se creó la deuda */}
                    <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
                      <div className="flex items-start justify-between gap-3 mb-2.5">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="font-black text-sm text-slate-900 truncate">
                              {debt.name || 'Concepto acordado'}
                            </p>
                            {debt.current_balance <= 0 ? (
                              <span className="text-[10px] px-2.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-bold">
                                ✓ Liquidado
                              </span>
                            ) : (debt.total_amount - debt.current_balance > 0) ? (
                              <span className="text-[10px] px-2.5 py-0.5 bg-blue-100 text-blue-800 rounded-full font-bold">
                                {Math.min(100, Math.round(((debt.total_amount - debt.current_balance) / (debt.total_amount || 1)) * 100))}% cubierto
                              </span>
                            ) : (
                              <span className="text-[10px] px-2.5 py-0.5 bg-slate-100 text-slate-500 rounded-full font-medium">
                                Pendiente
                              </span>
                            )}
                            <span className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full font-medium">
                              Concepto asignado
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500">
                            {debt.description || (debt.debt_direction !== 'they_owe_me' ? 'Concepto fijado por quien solicita el pago' : 'Monto total solicitado')}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-black text-sm text-slate-900">
                            {formatCurrency(debt.total_amount)}
                          </p>
                          {debt.total_amount - debt.current_balance > 0 && (
                            <p className="text-[10px] text-blue-600 font-bold">
                              Abonado: {formatCurrency(Math.max(0, debt.total_amount - debt.current_balance))}
                            </p>
                          )}
                        </div>
                      </div>
                      {/* Barra de progreso */}
                      <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${debt.current_balance <= 0 ? 'bg-emerald-500' : 'bg-indigo-600'}`}
                          style={{ width: `${Math.min(100, Math.round(((debt.total_amount - debt.current_balance) / (debt.total_amount || 1)) * 100))}%` }}
                        />
                      </div>
                    </div>

                    {/* Si es creador: invitación para desglosar en múltiples artículos si lo desea */}
                    {isCreator && (
                      <div className="p-4 bg-slate-50 border border-dashed border-slate-200 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
                        <div>
                          <p className="text-xs font-bold text-slate-700">¿Deseas desglosar en artículos específicos?</p>
                          <p className="text-[11px] text-slate-400">Puedes dividir los {formatCurrency(debt.total_amount)} en conceptos individuales (ej. Cena, Celular...)</p>
                        </div>
                        <button
                          onClick={() => { setEditingItem(null); setShowItemModal(true); }}
                          className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-sm active:scale-95 transition-all shrink-0"
                        >
                          + Desglosar Conceptos
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {(() => {
                      // Sumar pagos directos asociados a cada concepto
                      const directPaymentsByItem = {};
                      let genericPool = 0;
                      movements.forEach(m => {
                        if (m.type === 'payment') {
                          if (m.debt_item_ids && Array.isArray(m.debt_item_ids) && m.debt_item_ids.length > 0) {
                            const splitAmount = (m.amount || 0) / m.debt_item_ids.length;
                            m.debt_item_ids.forEach(itemId => {
                              directPaymentsByItem[itemId] = (directPaymentsByItem[itemId] || 0) + splitAmount;
                            });
                          } else if (m.debt_item_id) {
                            directPaymentsByItem[m.debt_item_id] = (directPaymentsByItem[m.debt_item_id] || 0) + (m.amount || 0);
                          } else {
                            genericPool += (m.amount || 0);
                          }
                        }
                      });

                      let remainingPool = genericPool;
                      return items.map((item, idx) => {
                        const direct = directPaymentsByItem[item.id] || 0;
                        const neededAfterDirect = Math.max(0, item.amount - direct);
                        const fromGeneric = Math.min(neededAfterDirect, Math.max(0, remainingPool));
                        remainingPool -= fromGeneric;
                        const covered = Math.min(item.amount, direct + fromGeneric);
                        const isCovered = covered >= item.amount && item.amount > 0;
                        const isPartial = covered > 0 && covered < item.amount;
                        const pct = item.amount > 0 ? Math.min((covered / item.amount) * 100, 100) : 0;

                        return (
                          <SwipeableItem
                            key={item.id}
                            item={item}
                            covered={covered}
                            isCovered={isCovered}
                            isPartial={isPartial}
                            pct={pct}
                            isFirstItem={idx === 0}
                            isReadOnly={!isCreator}
                            isOpen={activeSwipeId === item.id}
                            onOpen={() => handleItemOpen(item.id)}
                            onClose={() => setActiveSwipeId(null)}
                            onEdit={(it) => { setEditingItem(it); setShowItemModal(true); }}
                            onDelete={(itemId) => setConfirmDelete({
                              type: 'item',
                              id: itemId,
                              title: '¿Eliminar este concepto?',
                              message: `Se eliminará "${item.title}" (${formatCurrency(item.amount)}) del desglose.`
                            })}
                          />
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Comentarios del acreedor */}
            {(shareUrl || comments.length > 0) && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-slate-400" />
                  <h2 className="font-black text-slate-800 text-base">Comentarios del Acreedor</h2>
                </div>
                {comments.length === 0
                  ? <div className="text-center py-6 bg-white rounded-3xl border border-slate-100"><p className="text-slate-400 text-sm">El acreedor aún no ha comentado</p></div>
                  : <div className="space-y-2.5">{comments.map(c => (
                      <div key={c.id} className={`p-4 rounded-2xl ${c.is_agreement ? 'bg-emerald-50 border border-emerald-200' : 'bg-white border border-slate-100 shadow-sm'}`}>
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <p className="font-bold text-sm text-slate-800">{c.author_name}</p>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {c.is_agreement && <span className="text-[10px] px-2 py-0.5 bg-emerald-500 text-white rounded-full font-bold">✓ De acuerdo</span>}
                            {c.comment_type === 'inconforme' && (
                              c.is_resolved ? (
                                <span className="text-[10px] px-2 py-0.5 bg-emerald-600 text-white rounded-full font-bold">
                                  ✓ Resuelto
                                </span>
                              ) : (
                                <span className="text-[10px] px-2 py-0.5 bg-amber-500 text-white rounded-full font-bold">
                                  ⚠️ Aclaración
                                </span>
                              )
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-slate-600">{c.content}</p>
                        {c.is_resolved && c.resolved_by_movement_description && (
                          <div className="mt-2 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-xl text-[11px] text-emerald-800 font-medium">
                            ✓ {c.resolved_by_movement_description} ({c.resolved_at?.split('T')[0]})
                          </div>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1">{c.created_at?.split('T')[0]}</p>
                      </div>
                    ))}</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modales */}
      {showMovModal && (
        <MovementModal
          debt={debt}
          movementToEdit={editingMovement}
          comments={comments}
          items={items}
          movements={movements}
          onClose={() => { setShowMovModal(false); setEditingMovement(null); }}
          onSaved={fetchAll}
        />
      )}
      {showItemModal && (
        <ItemModal
          debt={debt}
          itemToEdit={editingItem}
          onClose={() => { setShowItemModal(false); setEditingItem(null); }}
          onSaved={fetchAll}
        />
      )}
      {showEditModal && <DebtModal debt={debt} onClose={() => setShowEditModal(false)} onSaved={fetchAll} />}
      {showSendReceiptModal && <SendReceiptModal debt={debt} totalPaid={totalPaid} onClose={() => setShowSendReceiptModal(false)} />}
      {viewingReceipt && <ReceiptModal receiptUrl={viewingReceipt} onClose={() => setViewingReceipt(null)} />}
      {confirmDelete && (
        <ConfirmDeleteModal
          title={confirmDelete.title}
          message={confirmDelete.message}
          onConfirm={executeDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      {confirmVerifyMovement && (
        <ConfirmVerifyModal
          movement={confirmVerifyMovement}
          loading={verifyingId === confirmVerifyMovement.id}
          onConfirm={() => handleVerifyMovement(confirmVerifyMovement)}
          onCancel={() => setConfirmVerifyMovement(null)}
        />
      )}
    </Layout>
  );
};

// ─── Vista Principal de Deudas ───────────────────────────
const Debts = () => {
  const { token, user } = useContext(AuthContext);
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'paid'
  const [directionTab, setDirectionTab] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('tab');
      if (p === 'they_owe_me' || p === 'i_owe') return p;
    } catch {}
    return 'i_owe';
  });

  const hasAutoOpenedDebt = useRef(false);

  const debtsByDirection = debts.filter(d => {
    if (directionTab === 'they_owe_me') {
      return d.debt_direction === 'they_owe_me';
    }
    return d.debt_direction !== 'they_owe_me';
  });

  const filtered = debtsByDirection.filter(d => {
    if (filter === 'all') return true;
    return d.status === filter;
  });

  const totalPending = debtsByDirection.filter(d => d.status === 'active').reduce((s, d) => s + d.current_balance, 0);

  const fetchDebts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/debts`, { headers: { Authorization: `Bearer ${token}` } });
      setDebts(res.data);
      
      // Auto-open debt once if debtId param is present
      try {
        if (!hasAutoOpenedDebt.current) {
          const debtIdParam = new URLSearchParams(window.location.search).get('debtId');
          if (debtIdParam && res.data?.length > 0) {
            const found = res.data.find(d => d.id === debtIdParam);
            if (found) {
              hasAutoOpenedDebt.current = true;
              setSelectedDebt(found);
            }
          }
        }
      } catch {}
    } catch {
      showToast.error('Error al cargar deudas');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('tab');
      if (p === 'they_owe_me' || p === 'i_owe') {
        setDirectionTab(p);
      }
    } catch {}
    fetchDebts();
  }, [fetchDebts]);

  // Listener para refrescar deudas automáticamente si se crea o elimina un gasto compartido
  useEffect(() => {
    const handleTransactionAdded = () => {
      fetchDebts();
    };
    window.addEventListener('transaction-added', handleTransactionAdded);
    return () => window.removeEventListener('transaction-added', handleTransactionAdded);
  }, [fetchDebts]);

  const handleBackFromDetail = () => {
    hasAutoOpenedDebt.current = true;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('debtId');
      window.history.replaceState({}, '', url.pathname + url.search);
    } catch {}
    setSelectedDebt(null);
  };

  const handleDebtDeleted = id => {
    handleBackFromDetail();
    setDebts(prev => prev.filter(d => d.id !== id));
  };

  if (selectedDebt) {
    return <DebtDetail debt={selectedDebt} onBack={handleBackFromDetail} onDeleted={handleDebtDeleted} />;
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6 pb-28">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-md shadow-rose-500/30">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Control de Deudas</h1>
              <p className="text-xs text-slate-500 font-medium">Planes de pago, intereses sobre saldo insoluto y deudas compartidas</p>
            </div>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="hidden sm:flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-rose-500 to-pink-600 text-white font-bold rounded-2xl shadow-lg shadow-rose-500/25 active:scale-95 transition-all"
          >
            <Plus className="w-5 h-5" /> Nueva Deuda
          </button>
        </div>

        {/* Pestañas Principales: Debo vs Me deben */}
        <div className="flex bg-slate-200/70 p-1.5 rounded-2xl mb-4 max-w-md">
          <button
            type="button"
            onClick={() => setDirectionTab('i_owe')}
            className={`flex-1 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
              directionTab === 'i_owe'
                ? 'bg-white text-rose-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <span>🔴 Debo (Mis Deudas)</span>
            <span className="text-[10px] px-1.5 py-0.2 bg-rose-100 text-rose-700 rounded-full font-black">
              {debts.filter(d => d.debt_direction !== 'they_owe_me').length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setDirectionTab('they_owe_me')}
            className={`flex-1 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
              directionTab === 'they_owe_me'
                ? 'bg-white text-emerald-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <span>🟢 Me deben (Por cobrar)</span>
            <span className="text-[10px] px-1.5 py-0.2 bg-emerald-100 text-emerald-700 rounded-full font-black">
              {debts.filter(d => d.debt_direction === 'they_owe_me').length}
            </span>
          </button>
        </div>

        {debtsByDirection.length > 0 && (
          <div className={`mb-6 p-5 sm:p-6 rounded-3xl text-white shadow-xl ${
            directionTab === 'they_owe_me'
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 shadow-emerald-500/20'
              : 'bg-gradient-to-r from-rose-500 to-pink-600 shadow-rose-500/20'
          }`}>
            <p className="text-white/70 text-xs font-bold uppercase tracking-wider mb-1">
              {directionTab === 'they_owe_me' ? 'Total por Cobrar' : 'Total Pendiente por Pagar'}
            </p>
            <p className="text-3xl sm:text-4xl font-black">{formatCurrency(totalPending)}</p>
            <p className="text-white/80 text-xs mt-1.5 font-medium">
              {debtsByDirection.filter(d => d.status === 'active').length} {directionTab === 'they_owe_me' ? 'cuenta(s) activa(s)' : 'deuda(s) activa(s)'}
            </p>
          </div>
        )}

        {/* Filtros */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1 scrollbar-none touch-pan-x">
          {[['all','Todas'],['active','En progreso'],['paid','Liquidadas']].map(([k,l]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all ${filter === k ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200'}`}>
              {l}
            </button>
          ))}
        </div>

        {/* Lista en Grid */}
        {loading
          ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-slate-100 rounded-3xl animate-pulse" />)}</div>
          : filtered.length === 0
            ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-9 h-9 text-slate-300" />
                </div>
                <p className="text-slate-500 font-bold mb-1">
                  Sin {directionTab === 'they_owe_me' ? 'cuentas por cobrar' : 'deudas'} {filter !== 'all' ? 'en esta categoría' : 'registradas'}
                </p>
                <p className="text-slate-400 text-sm mb-6">
                  {directionTab === 'they_owe_me'
                    ? 'Registra préstamos que hiciste a otros para darles seguimiento'
                    : 'Crea tu primer plan de pagos con el botón de abajo'}
                </p>
                <button onClick={() => setShowCreateModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-2xl shadow-lg active:scale-95 transition-all">
                  {directionTab === 'they_owe_me' ? '+ Registrar Cuenta por Cobrar' : '+ Crear Deuda'}
                </button>
              </div>
            )
            : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map(debt => {
                  const effectiveTotal = Math.max(debt.total_amount, debt.current_balance);
                  const paid = Math.max(effectiveTotal - debt.current_balance, 0);
                  const progress = effectiveTotal > 0 ? Math.min(Math.max((paid / effectiveTotal) * 100, 0), 100) : 0;
                  const statusCfg = STATUS_CONFIG[debt.status] || STATUS_CONFIG.active;
                  const CreditorIcon = CREDITOR_ICONS[debt.creditor_type]?.icon || CreditCard;
                  const freqLabel = FREQUENCY_LABELS[debt.payment_frequency];
                  const isShared = debt.collaborators && debt.collaborators.length > 0;

                  return (
                    <button key={debt.id} onClick={() => setSelectedDebt(debt)}
                      className="w-full text-left bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md hover:border-slate-200 active:scale-[0.98] transition-all flex flex-col justify-between p-5">
                      <div>
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${debt.color}20` }}>
                            <CreditorIcon className="w-5 h-5" style={{ color: debt.color }} />
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-black text-slate-900 text-base">{formatCurrency(debt.current_balance)}</p>
                            <p className="text-[11px] text-slate-400 font-medium">
                              {debt.has_interest ? 'saldo para liquidar' : `resta de ${formatCurrency(effectiveTotal)}`}
                            </p>
                          </div>
                        </div>

                        <div>
                          <p className="font-black text-slate-900 text-base mb-1 truncate">{debt.name}</p>
                          <div className="flex items-center gap-1.5 flex-wrap mb-2">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${statusCfg.color}`}>{statusCfg.label}</span>
                            {debt.has_interest && (
                              <span className="text-[10px] font-bold text-amber-900 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-0.5">
                                <Percent className="w-2.5 h-2.5" /> {debt.interest_rate}% m.
                              </span>
                            )}
                            {isShared && (
                              <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Users className="w-3 h-3" /> Compartida ({debt.collaborators.length})
                              </span>
                            )}
                            {debt.agreement_status && (
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${debt.agreement_status === 'conforme' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                                {debt.agreement_status === 'conforme' ? '✓ Conforme' : '⚠️ Aclaración'}
                              </span>
                            )}
                            {debt.payment_frequency && debt.payment_frequency !== 'none' && (
                              <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                                {freqLabel} {debt.installment_amount ? `• ${formatCurrency(debt.installment_amount)}` : ''}
                              </span>
                            )}
                          </div>

                          {/* Desglose directo de Capital e Interés en la tarjeta exterior */}
                          {debt.has_interest && (
                            <div className="grid grid-cols-2 gap-1.5 bg-amber-50/70 border border-amber-100/80 rounded-xl p-2 text-[11px] mt-1 mb-1">
                              <div>
                                <span className="text-slate-400 text-[10px] block font-semibold uppercase tracking-wider">Capital</span>
                                <span className="font-black text-slate-800">{formatCurrency(debt.current_principal || 0)}</span>
                              </div>
                              <div>
                                <span className="text-amber-700/80 text-[10px] block font-semibold uppercase tracking-wider">Interés</span>
                                <span className="font-black text-amber-800">{formatCurrency(debt.accumulated_interest || 0)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-slate-50">
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-1">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: debt.color }} />
                        </div>
                        <div className="flex items-center justify-between text-[10px]">
                          {(!debt.is_mirror && debt.is_creator !== false && !String(debt.description || '').startsWith('Deuda vinculada automáticamente desde PGastos') && debt.share_token) ? (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                const url = `${window.location.origin}/public/debt/${debt.share_token}`;
                                navigator.clipboard?.writeText(url);
                                showToast.success('Liga pública copiada');
                              }}
                              className="text-indigo-600 hover:text-indigo-800 font-bold flex items-center gap-1 bg-indigo-50 hover:bg-indigo-100 px-2 py-0.5 rounded-md transition-colors cursor-pointer"
                              title="Copiar liga pública"
                            >
                              <Link className="w-2.5 h-2.5" /> Copiar Liga
                            </span>
                          ) : <span />}
                          <p className="text-slate-400 text-right font-bold">{progress.toFixed(0)}% pagado</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
      </div>

      {/* FAB en móvil */}
      <button onClick={() => setShowCreateModal(true)}
        className="sm:hidden fixed bottom-24 right-5 w-14 h-14 bg-gradient-to-br from-rose-500 to-pink-600 text-white rounded-full shadow-xl shadow-rose-500/30 flex items-center justify-center active:scale-90 transition-all z-20">
        <Plus className="w-7 h-7" />
      </button>

      {/* Modal Crear Deuda */}
      {showCreateModal && (
        <DebtModal
          initialDirection={directionTab}
          onClose={() => setShowCreateModal(false)}
          onSaved={fetchDebts}
        />
      )}
    </Layout>
  );
};

export default Debts;
